import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../contexts/TranslationContext';
import { ordersApi, purchasesApi, catalogueApi, invoiceSyncApi, organizationsApi } from '../lib/api';
import { useReorderQuote, ReorderQuoteCard } from '../components/ReorderQuoteCard';
import { formatDate, formatCurrency, formatRelativeTime, oldestPendingSince } from '../lib/utils';
import { Header } from '../components/Header';
import { OrderStatusBadge, FinanceStatusBadge, InvoicePaymentBadge, TestPressBadge } from '../components/StatusBadge';
import { DemoBadge } from '../components/DemoBadge';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { MediaDiscIcon } from '../components/MediaDiscIcons';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '../components/ui/popover';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from '../components/ui/command';
import { HoverCard, HoverCardTrigger, HoverCardContent } from '../components/ui/hover-card';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
    Plus, 
    Package, 
    Clock, 
    CheckCircle, 
    AlertCircle,
    ChevronRight,
    FileText,
    Search,
    Palette,
    Check,
    X,
    Filter,
    ChevronsUpDown,
    AlertTriangle,
    Shield,
    DollarSign,
    Truck,
    Ban,
    Loader2,
    RefreshCw,
    Disc,
    Library,
    Trash2,
    Sparkles,
    Zap,
    ArrowUp,
    ArrowDown,
    ArrowUpDown,
    MessageSquare,
    CalendarClock
} from 'lucide-react';
import axios from 'axios';
import TodayProgress from '../components/TodayProgress';
import OrderHealthScore from '../components/OrderHealthScore';
import CustomerActionCenter from '../components/CustomerActionCenter';
import CustomerActivityFeed from '../components/CustomerActivityFeed';
import NeedsAttentionResolvePanel from '../components/NeedsAttentionResolvePanel';
import { NCBReminderMenu } from '../components/NCBReminderMenu';

import { API_BASE } from '../lib/apiBase';
const API = `${API_BASE}/api`;

// Format order number as 6 digits
const formatOrderNumber = (num) => {
    if (!num) return '------';
    return String(num).padStart(6, '0');
};

// Clickable sortable column header for the admin orders table.
// Shows an active up/down arrow on the current sort column and a neutral arrow on the rest.
// Returns BOTH the inner button and the aria-sort value so the parent <th> can mirror it
// for broader screen-reader compatibility (WCAG/WAI-ARIA convention).
const getAriaSort = (column, sortBy, sortDir) => {
    if (sortBy !== column) return 'none';
    return sortDir === 'asc' ? 'ascending' : 'descending';
};

const SortableHead = ({ column, label, sortBy, sortDir, onSort, className = '', testId }) => {
    const isActive = sortBy === column;
    const Icon = isActive ? (sortDir === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
    return (
        <button
            type="button"
            onClick={() => onSort(column)}
            className={`flex items-center gap-1 text-left hover:text-slate-700 transition-colors w-full ${isActive ? 'text-slate-700' : 'text-slate-500'}`}
            data-testid={testId || `sort-${column}`}
            aria-sort={getAriaSort(column, sortBy, sortDir)}
        >
            <span>{label}</span>
            <Icon className={`h-3 w-3 ${isActive ? 'opacity-100' : 'opacity-40'} ${className}`} />
        </button>
    );
};

// Artwork status badge (uses Artwork Job status from repro_jobs)
const ArtworkStatusBadge = ({ status, reviewStatus, rejectedCount, orderId, onClick, reusable }) => {
    const { t } = useTranslation();
    // Per-file review status overrides job status for display
    if (reviewStatus === 'rejected') {
        const label = rejectedCount > 0
            ? t('status.fix_artwork_count', 'Fix artwork ({n})', { n: rejectedCount })
            : t('status.fix_artwork', 'Fix artwork');
        return (
            <button 
                onClick={(e) => { e.stopPropagation(); onClick?.(); }}
                className="asset-badge cursor-pointer hover:opacity-80 transition-opacity"
                style={{background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', fontWeight: 600}}
                data-testid="artwork-badge-action"
            >
                {label}
            </button>
        );
    }
    if (reviewStatus === 'approved') {
        return <span className="asset-badge asset-approved" data-testid="artwork-badge">{t('status.approved', 'Approved')}</span>;
    }

    // Reorder reuse path: catalogue entry has approved artwork from a previous
    // order and no new job has been started yet — surface "Ready" so the
    // customer knows nothing has to be re-uploaded for this order.
    if (!status && reusable) {
        return <span className="asset-badge asset-ready" data-testid="artwork-badge-ready">{t('status.ready', 'Ready')}</span>;
    }

    if (!status) {
        return <span className="asset-badge asset-missing" data-testid="artwork-badge-missing">{t('status.missing', 'Missing')}</span>;
    }
    
    // Awaiting_Approval: render actionable CTA button (deep-link to artwork review)
    if (status === 'Awaiting_Approval') {
        return (
            <button
                onClick={(e) => { e.stopPropagation(); onClick?.(); }}
                className="asset-badge cursor-pointer hover:opacity-80 transition-opacity"
                style={{background: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d', fontWeight: 600}}
                data-testid="artwork-badge-review-action"
            >
                {t('status.review_artwork', 'Review Artwork')}
            </button>
        );
    }
    
    const statusConfig = {
        'Open':        { label: t('status.uploaded', 'Uploaded'),       className: 'asset-uploaded' },
        'Assigned':    { label: t('status.in_review', 'In Review'),     className: 'asset-in-progress' },
        'In_Progress': { label: t('status.in_review', 'In Review'),     className: 'asset-in-progress' },
        'Approved':    { label: t('status.approved', 'Approved'),       className: 'asset-approved' },
        'Completed':   { label: t('status.completed', 'Completed'),     className: 'asset-completed' },
    };
    
    const config = statusConfig[status] || { label: status.replace(/_/g, ' '), className: 'asset-none' };
    
    return (
        <span className={`asset-badge ${config.className}`} data-testid="artwork-badge">
            {config.label}
        </span>
    );
};

// Delivery status display component
const DeliveryStatus = ({ order }) => {
    const { t } = useTranslation();

    // Test Press phase: while a TP is active (pre-arrival) and has
    // its own expected arrival date, THAT is the currently relevant
    // delivery milestone — the final production delivery is still
    // weeks away and only becomes the headline once the TP is
    // approved. We only yield to the production surface when the
    // physical production shipment is already in transit (carrier
    // tracking), which trumps every other delivery signal.
    const tpStatus = order.test_press_status;
    const tpArrival = order.test_press_expected_arrival;
    const tpIsActivePreArrival = (
        tpStatus === 'required'
        || tpStatus === 'ordered'
        || tpStatus === 'confirmed'
        || tpStatus === 'dispatched'
    );
    if (tpIsActivePreArrival && tpArrival && !order.carrier_tracking_status) {
        // Format the TP arrival from a bare YYYY-MM-DD without TZ drift.
        let dateStr = tpArrival;
        try {
            const [y, m, d] = String(tpArrival).split('-');
            dateStr = new Date(Date.UTC(+y, +m - 1, +d)).toLocaleDateString('en-US', {
                month: 'short', day: 'numeric', timeZone: 'UTC',
            });
        } catch (_e) { /* fall back to raw string */ }
        return (
            <span
                className="inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full border text-sky-700 bg-sky-50 border-sky-200"
                data-testid={`delivery-tp-expected-${order.id}`}
                title={t('delivery.tp_tooltip', 'Test Press expected arrival — production delivery date will replace this once the TP is approved.')}
            >
                <span className="uppercase tracking-wide text-[9px] font-semibold">TP</span>
                <span className="tabular-nums">{dateStr}</span>
            </span>
        );
    }

    // 1. Carrier tracking wins. When Inbound Hub has pushed at least one
    //    tracking update matched to a destination on this order, we
    //    render the normalized shipment state + effective delivery
    //    date directly here. Delayed destinations get a rose chip. No
    //    external DSV link — customers act on FLOW data only.
    if (order.carrier_tracking_status) {
        const STATUS_LABEL = {
            booked: t('delivery.tracking.booked', 'Booked'),
            in_transportation: t('delivery.tracking.in_transit', 'In transit'),
            at_dispatching_center: t('delivery.tracking.at_center', 'At dispatching center'),
            out_for_delivery: t('delivery.tracking.out_for_delivery', 'Out for delivery'),
            delivered: t('delivery.tracking.delivered', 'Delivered'),
        };
        const isDelayed = order.carrier_tracking_delay_state === 'delayed';
        const eta = order.carrier_tracking_effective_date;
        const delivered = order.carrier_tracking_status === 'delivered';
        const chipCls = delivered
            ? 'text-emerald-700 bg-emerald-50 border-emerald-200'
            : isDelayed
                ? 'text-rose-700 bg-rose-50 border-rose-200'
                : 'text-indigo-700 bg-indigo-50 border-indigo-200';
        return (
            <span
                className={`inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full border ${chipCls}`}
                data-testid={`carrier-tracking-${order.id}`}
                title={
                    isDelayed
                        ? t('delivery.tracking.delayed_by', 'Delayed by {n} day(s)', { n: order.carrier_tracking_delay_days || 1 })
                        : ''
                }
            >
                <Truck className="h-3 w-3" />
                <span>{STATUS_LABEL[order.carrier_tracking_status] || order.carrier_tracking_status}</span>
                {eta && (
                    <>
                        <span className="text-slate-300">·</span>
                        <span className="tabular-nums">{formatDate(eta)}</span>
                    </>
                )}
                {isDelayed && !delivered && (
                    <span className="ml-1 uppercase tracking-wide text-[9px] font-semibold">
                        {t('delivery.tracking.delayed', 'Delayed')}
                    </span>
                )}
            </span>
        );
    }

    // If a manual delivery_date exists, it's authoritative — show it.
    if (order.delivery_date) {
        return (
            <span className="text-sm text-slate-700 font-medium">
                {formatDate(order.delivery_date)}
            </span>
        );
    }

    // Inbound dispatch-note date fallback. Surfaces the planned arrival
    // date from a linked inbound dispatch document BEFORE the admin
    // clicks "Approve Dispatch" — previously this date was hidden inside
    // supplier_confirmations. Italic + tooltip to make the source clear.
    if (order.dispatch_note_date) {
        return (
            <span
                className="text-sm text-slate-700 italic font-medium"
                title="Source: linked dispatch note (not yet approved)"
                data-testid={`dispatch-note-date-${order.id}`}
            >
                {formatDate(order.dispatch_note_date)}
                <span className="ml-1 text-[10px] uppercase tracking-wide text-slate-400 not-italic">
                    note
                </span>
            </span>
        );
    }
    
    // Check supplier confirmation status
    const supplierConf = order.supplier_confirmation;
    
    // Order is Draft or not submitted yet
    if (order.status === 'Draft') {
        return <span className="text-xs text-slate-400">—</span>;
    }
    
    // If supplier mode is disabled, no delivery tracking
    if (order.supplier_mode === 'none') {
        return <span className="text-xs text-slate-400">{t('dashboard.direct', 'Direct')}</span>;
    }
    
    // Check if PO has been sent (order has been submitted and moved to In Progress or beyond)
    const hasPoSent = order.status !== 'Draft' && order.status !== 'Submitted';
    
    // If no supplier confirmation data
    if (!supplierConf) {
        if (hasPoSent) {
            return (
                <span className="inline-flex items-center gap-1 text-xs text-amber-600">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Awaiting confirmation
                </span>
            );
        }
        return (
            <span className="text-xs text-slate-400">{t('dashboard.pending', 'Pending')}</span>
        );
    }
    
    // Supplier confirmation exists but no delivery date yet
    if (supplierConf.status === 'Pending' || supplierConf.status === 'Mismatch') {
        return (
            <span className="inline-flex items-center gap-1 text-xs text-amber-600">
                <Loader2 className="h-3 w-3 animate-spin" />
                Awaiting confirmation
            </span>
        );
    }
    
    // Confirmed but no delivery date set
    if (supplierConf.status === 'Approved' && !order.delivery_date) {
        return (
            <span className="inline-flex items-center gap-1 text-xs text-blue-600">
                <Clock className="h-3 w-3" />
                ETA pending
            </span>
        );
    }
    
    return <span className="text-xs text-slate-400">—</span>;
};

export default function DashboardPage() {
    const { user, isAdmin, activeCompany } = useAuth();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [orders, setOrders] = useState([]);
    const [purchases, setPurchases] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [newOrderTitle, setNewOrderTitle] = useState('');
    const [newOrderDescription, setNewOrderDescription] = useState('');
    const [newOrderCatalogueNumber, setNewOrderCatalogueNumber] = useState('');
    const [isCreating, setIsCreating] = useState(false);
    
    // Order type for new orders
    const [orderType, setOrderType] = useState('new_production');
    const [catalogueEntries, setCatalogueEntries] = useState([]);
    const [selectedCatalogueEntry, setSelectedCatalogueEntry] = useState(null);
    const [selectedReorderPolicy, setSelectedReorderPolicy] = useState(null);

    // Whenever the selected catalogue entry changes, fetch its
    // effective reorder policy (entry override → family default) so
    // the modal enforces the right minimum and prefills a sensible qty.
    useEffect(() => {
        if (!selectedCatalogueEntry?.id) {
            setSelectedReorderPolicy(null);
            return;
        }
        let cancelled = false;
        catalogueApi.reorderPolicy(selectedCatalogueEntry.id).then(
            (res) => {
                if (cancelled) return;
                setSelectedReorderPolicy(res.data);
                // Reset the qty field to the family/entry minimum so
                // it never leaves the modal below the policy floor.
                setReorderQuantity(String(res.data.min_qty));
            },
            () => { if (!cancelled) setSelectedReorderPolicy(null); },
        );
        return () => { cancelled = true; };
    }, [selectedCatalogueEntry?.id]);
    const [catalogueSearchOpen, setCatalogueSearchOpen] = useState(false);
    const [reorderQuantity, setReorderQuantity] = useState('250');
    
    // Product type selector modal state
    const [showProductTypeModal, setShowProductTypeModal] = useState(false);
    const [enabledOrderTypes, setEnabledOrderTypes] = useState(['vinyl']);
    const [availableOrderTypes, setAvailableOrderTypes] = useState([]);
    
    // Platform settings (for new customer landing page toggle)
    const [platformSettings, setPlatformSettings] = useState({ show_new_customer_landing: true });
    
    // Actions summary for admin attention items
    const [actionsSummary, setActionsSummary] = useState(null);
    
    // Tab state for customers (replaces status filter). Defaults to
    // "All" so the first-open My Orders view shows the full set
    // (In Progress, Completed, Draft remain selectable). If the URL
    // explicitly carries ?tab=…, that still wins so deep-links and
    // mid-session navigation keep working.
    const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'all');
    
    // Filter states - initialized from URL params (admin only)
    const [customerFilter, setCustomerFilter] = useState(searchParams.get('customer') || 'all');
    // Legacy URL support: `?showCompleted=true` from old bookmarks maps
    // to the new "Completed" status pill.
    const [statusFilter, setStatusFilter] = useState(() => {
        const s = searchParams.get('status');
        if (s) return s;
        if (searchParams.get('showCompleted') === 'true') return 'completed';
        return 'all';
    });
    const [productTypeFilter, setProductTypeFilter] = useState(searchParams.get('type') || 'all');
    const [orderTypeFilter, setOrderTypeFilter] = useState(searchParams.get('order_type') || 'all');
    // (legacy `showCompletedOrders` state removed 2026-02-19 — folded
    // into the unified `statusFilter` pill "Completed". Legacy
    // ?showCompleted=true URLs are promoted to ?status=completed above.)
    const [attentionFilters, setAttentionFilters] = useState(() => {
        const filters = [];
        if (searchParams.get('artwork') === 'pending') filters.push('artwork');
        if (searchParams.get('audio') === 'missing') filters.push('audio');
        if (searchParams.get('ncb') === 'pending') filters.push('ncb');
        if (searchParams.get('supplier') === 'pending') filters.push('supplier');
        return filters;
    });
    const [customerSearchOpen, setCustomerSearchOpen] = useState(false);

    // Customer "quick filter" — set when the customer clicks a row in
    // Your Action Center (e.g. "3 artworks ready for your approval").
    // Overrides the status pills so the table shows exactly the items
    // that were clicked, with a dismissable chip above the table.
    // Values: 'artwork-approval' | 'artwork-revision' | 'missing-master'
    //       | 'quote' | 'unpaid-invoice'
    const [quickFilter, setQuickFilter] = useState(searchParams.get('filter') || null);

    // Keep quickFilter in sync with the URL — react-router-dom re-runs
    // this component with new search params instead of remounting, so
    // useState's initial value won't pick up subsequent Action Center
    // clicks on the same page. This effect closes that loop.
    useEffect(() => {
        const fromUrl = searchParams.get('filter') || null;
        setQuickFilter(fromUrl);
    }, [searchParams]);

    // Admin table sort state — defaults to Order # descending. Persisted in URL params.
    const [sortBy, setSortBy] = useState(searchParams.get('sortBy') || 'order_number');
    const [sortDir, setSortDir] = useState(searchParams.get('sortDir') || 'desc');

    const handleSort = (column) => {
        if (sortBy === column) {
            setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortBy(column);
            setSortDir('asc');
        }
    };
    
    // Delete draft order state (customer only)
    const [deleteOrderId, setDeleteOrderId] = useState(null);
    const [deleteOrderTitle, setDeleteOrderTitle] = useState('');
    const [isDeleting, setIsDeleting] = useState(false);
    const [showOpenOrderBreakdown, setShowOpenOrderBreakdown] = useState(false);
    // Either 'active' (Total Active Order Value) or 'uninvoiced' (Open
    // Uninvoiced Order Value) — controls which metric the breakdown
    // modal is displaying.
    const [breakdownMetric, setBreakdownMetric] = useState('active');
    // Sort state for the Total Active Order Value / Open Uninvoiced
    // breakdown table. `field` can be 'dkk' | 'gross_profit' | 'margin'.
    // Default: DKK descending (matches the previous behaviour before the
    // Gross Margin column was added).
    const [breakdownSort, setBreakdownSort] = useState({ field: 'dkk', dir: 'desc' });
    
    // Credit status (customer only)
    const [creditStatus, setCreditStatus] = useState(null);

    useEffect(() => {
        fetchData();
        fetchPlatformSettings();
    }, []);

    // Re-fetch enabled order types whenever the active company / user identity changes.
    // Without this dependency the initial mount can fire BEFORE auth context resolves,
    // leaving state at the default ['vinyl'] and skipping the product-type chooser
    // even when multiple types are enabled. Also handles super-admin impersonation
    // (switching active_company should refresh the chooser options).
    const orderTypesCompanyId = activeCompany?.id || user?.organization_id;
    useEffect(() => {
        if (orderTypesCompanyId) {
            fetchEnabledOrderTypes();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orderTypesCompanyId]);

    // Sync filters/tab to URL
    useEffect(() => {
        const params = new URLSearchParams();
        
        if (!isAdmin) {
            // Customer: sync tab + quick-filter. Preserving `filter` here
            // is essential — without it this effect wipes the URL param
            // right after Action Center navigates in.
            // Default tab is "all" — omit it from the URL to keep the
            // clean /dashboard address.
            if (activeTab && activeTab !== 'all') params.set('tab', activeTab);
            if (quickFilter) params.set('filter', quickFilter);
        } else {
            // Admin: sync all filters
            if (customerFilter && customerFilter !== 'all') params.set('customer', customerFilter);
            if (statusFilter && statusFilter !== 'all') params.set('status', statusFilter);
            if (productTypeFilter && productTypeFilter !== 'all') params.set('type', productTypeFilter);
            if (orderTypeFilter && orderTypeFilter !== 'all') params.set('order_type', orderTypeFilter);
            // Legacy ?showCompleted URL param no longer written — kept for
            // read-time backwards compat only (see statusFilter init above).
            if (attentionFilters.includes('artwork')) params.set('artwork', 'pending');
            if (attentionFilters.includes('audio')) params.set('audio', 'missing');
            if (attentionFilters.includes('ncb')) params.set('ncb', 'pending');
            if (attentionFilters.includes('supplier')) params.set('supplier', 'pending');
            // Persist sort if not the default. When the user has explicitly chosen a column,
            // always emit BOTH sortBy and sortDir so the URL is self-explanatory and shareable.
            if (sortBy !== 'order_number' || sortDir !== 'desc') {
                params.set('sortBy', sortBy);
                params.set('sortDir', sortDir);
            }
        }
        
        setSearchParams(params, { replace: true });
    }, [customerFilter, statusFilter, productTypeFilter, orderTypeFilter, attentionFilters, activeTab, quickFilter, isAdmin, setSearchParams, sortBy, sortDir]);

    // Live reorder quote — uses the shared hook so the dashboard's
    // Quick-Reorder modal stays in lockstep with the Catalogue Library
    // detail modal. Same endpoint, same debounce, same render.
    const { quote: reorderQuote, loading: reorderQuoteLoading } = useReorderQuote(
        selectedCatalogueEntry?.id,
        reorderQuantity,
    );


    // Refresh only the actions summary — used by the Needs-Attention
    // resolve popover after each approve/duplicate/dismiss/snooze so
    // the counters update without a full page reload.
    const fetchActionsSummary = async () => {
        try {
            const actionsRes = await axios.get(`${API}/admin/actions-summary`);
            setActionsSummary(actionsRes.data);
        } catch (e) {
            console.error('Failed to fetch actions summary');
        }
    };

    const fetchData = async () => {
        try {
            const ordersRes = await ordersApi.getAll();
            
            // Fetch supplier confirmations for all orders (admin-only endpoint — skip for customers to avoid 403 spam)
            let ordersWithConf;
            if (isAdmin) {
                ordersWithConf = await Promise.all(
                    ordersRes.data.map(async (order) => {
                        try {
                            const confRes = await axios.get(`${API}/orders/${order.id}/supplier-confirmation`, {
                                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                            });
                            return { ...order, supplier_confirmation: confRes.data };
                        } catch (e) {
                            // No confirmation exists or other error — treat as missing
                            return { ...order, supplier_confirmation: null };
                        }
                    })
                );
            } else {
                // Customers don't need supplier_confirmation data (internal admin tracking)
                ordersWithConf = ordersRes.data.map(order => ({ ...order, supplier_confirmation: null }));
            }
            
            setOrders(ordersWithConf);
            
            // Fetch catalogue entries for customers (for reorder feature)
            if (!isAdmin) {
                try {
                    const catRes = await catalogueApi.getAll();
                    setCatalogueEntries(catRes.data);
                } catch (e) {
                    console.error('Failed to fetch catalogue entries');
                }
                // Fetch credit status for customer
                try {
                    const orgId = user?.active_company_id || user?.organization_id;
                    if (orgId) {
                        const creditRes = await invoiceSyncApi.getCreditStatus(orgId);
                        setCreditStatus(creditRes.data);
                    }
                } catch (e) {
                    // Not critical - just skip
                }
            }
            
            if (isAdmin) {
                const purchasesRes = await purchasesApi.getAll();
                setPurchases(purchasesRes.data);
                
                // Fetch UNIQUE customer organizations for filter dropdown.
                // Previously fetched users (one per contact person) which caused duplicate
                // companies in the dropdown when multiple users belonged to the same org.
                try {
                    const orgsRes = await organizationsApi.getAll();
                    // Backend already sorts by name asc, but enforce it here too in case the API changes.
                    const orgs = (orgsRes.data || [])
                        .filter(o => o && o.id && (o.name || '').trim())
                        .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
                    setCustomers(orgs);
                } catch (e) {
                    console.error('Failed to fetch customer organizations');
                }
                
                // Fetch actions summary for attention items
                await fetchActionsSummary();
            }
        } catch (error) {
            toast.error('Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateOrder = async (e) => {
        e.preventDefault();
        setIsCreating(true);

        try {
            if (orderType === 'reorder' && selectedCatalogueEntry) {
                // Reorder flow — delegate ENTIRELY to the canonical
                // ``POST /api/catalogue/{id}/reorder`` endpoint so that
                // product-type detection (vinyl / cd / dvd / bluray),
                // config-flow copy-over, source-order lookup, vinyl-vs-
                // simple-media payload shaping and quote gating stay in
                // ONE place. Previously the dashboard hand-built a
                // vinyl-only payload here which forced DVD/CD/Blu-ray
                // catalogue items into vinyl logic — that's the bug the
                // Catalogue Title reorder didn't have.
                const entry = selectedCatalogueEntry;
                const quantity = parseInt(reorderQuantity, 10) || 250;
                const res = await catalogueApi.reorder(entry.id, quantity);
                if (res.data.pricing_warning) {
                    toast.warning(res.data.pricing_warning);
                } else {
                    toast.success(res.data.message || 'Reorder created');
                }
                setIsCreateDialogOpen(false);
                resetCreateForm();
                navigate(`/orders/${res.data.order_id}`);
                return;
            }
            // Fresh production flow
            const orderData = {
                title: newOrderTitle,
                description: newOrderDescription,
                catalogue_number: newOrderCatalogueNumber,
                order_type: 'new_production',
            };

            const response = await ordersApi.create(orderData);
            setOrders([response.data, ...orders]);
            setIsCreateDialogOpen(false);
            resetCreateForm();
            toast.success('Order created successfully');
            navigate(`/orders/${response.data.id}/product`);
        } catch (error) {
            toast.error(error?.response?.data?.detail || 'Failed to create order');
        } finally {
            setIsCreating(false);
        }
    };
    
    const resetCreateForm = () => {
        setNewOrderTitle('');
        setNewOrderDescription('');
        setNewOrderCatalogueNumber('');
        setOrderType('new_production');
        setSelectedCatalogueEntry(null);
        setReorderQuantity('250');
        // reorderQuote is derived from the hook — clearing the selected
        // entry & quantity above is enough to reset the preview card.
    };

    // Quick create for new production - navigate to new order flow without creating order yet
    const handleQuickNewOrder = async () => {
        // Check how many order types are enabled for this customer
        if (enabledOrderTypes.length > 1) {
            // Show selector modal
            setShowProductTypeModal(true);
        } else if (enabledOrderTypes.length === 1) {
            // All families now share the same two-step order flow — the only
            // difference is the product_type passed via ?family=.
            const productType = enabledOrderTypes[0];
            const qs = productType === 'vinyl' ? '' : `?family=${productType}`;
            navigate(`/orders/new/product${qs}`);
        } else {
            // Fallback to vinyl
            navigate('/orders/new/product');
        }
    };
    
    // Handle product type selection from modal
    const handleProductTypeSelect = (productType) => {
        setShowProductTypeModal(false);
        // Unified two-step order flow (Order details → Production Files) for
        // every family. Product-specific steps/options are loaded server-side
        // from the flow config keyed on ?family=.
        const qs = productType === 'vinyl' ? '' : `?family=${productType}`;
        navigate(`/orders/new/product${qs}`);
    };
    
    // Fetch enabled order types for the current user's active company
    const fetchEnabledOrderTypes = async () => {
        const companyId = activeCompany?.id || user?.organization_id;
        if (!companyId) return;
        
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(
                `${API}/organizations/${companyId}/order-types`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setEnabledOrderTypes(response.data.enabled_order_types || ['vinyl']);
            setAvailableOrderTypes(response.data.available_order_types || []);
        } catch (error) {
            // Default to vinyl if fetch fails
            setEnabledOrderTypes(['vinyl']);
        }
    };
    
    // Fetch platform settings (for new customer landing page toggle)
    const fetchPlatformSettings = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(
                `${API}/admin/platform-settings`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setPlatformSettings(response.data);
        } catch (error) {
            // Default to showing landing page
            console.error('Failed to fetch platform settings:', error);
        }
    };

    // Attention filter helper functions (admin only)
    const hasArtworkIssue = (order) => {
        // Reorders reuse the catalogue's existing artwork and default to
        // Ready — never surface them under "missing artwork" regardless
        // of what `reusable_artwork` looks like on this specific record.
        if (order.order_type === 'reorder') return false;
        // Reorders that reuse the catalogue's approved artwork show "Ready"
        // and need no action — exclude from the Artwork attention bucket.
        if (order.reusable_artwork && !order.artwork_status) return false;
        // "Review Artwork" (Awaiting_Approval) belongs in the separate
        // "artwork awaiting approval" attention bucket, not in
        // "missing artwork".
        if (order.artwork_status === 'Awaiting_Approval') return false;
        // Per Test Press spec: while a Test Press is required but not
        // yet APPROVED, missing artwork is an expected intermediate
        // state and must NOT nag the admin. Flip only resumes once
        // ``test_press_status`` reaches ``approved`` (or the order
        // was never a TP order to begin with — ``not_required`` /
        // null / undefined). MUST mirror the backend predicate in
        // ``admin_actions.get_actions_summary``.
        const tp = order.test_press_status;
        if (tp && tp !== 'not_required' && tp !== 'approved') return false;
        if (!order.artwork_status && order.status !== 'Draft') return true;
        return false;
    };

    const hasMissingAudio = (order) => {
        // Reorders reuse the catalogue's existing master and default to
        // Ready — never surface them under "master not ready".
        if (order.order_type === 'reorder') return false;
        // Reorders that reuse the catalogue's approved master show "Ready"
        // and need no action — exclude from the Master attention bucket.
        if (order.reusable_master && !order.has_audio_master) return false;
        return !order.has_audio_master;
    };
    
    const hasNCBIssue = (order) => {
        return order.ncb_clearance?.status !== 'cleared' && order.status !== 'Draft';
    };
    
    const hasSupplierIssue = (order) => {
        // Attention/Supplier: actionable only when the order is actively
        // being produced ("In Progress") AND we still don't have a
        // confirmed delivery date from the supplier. Drafts/Submitted
        // (PO not sent yet) and orders that already have a delivery
        // date are not actionable by the supplier desk and so don't
        // belong on this attention list.
        if (order.status !== 'In Progress') return false;
        if (order.delivery_date) return false;
        return true;
    };
    
    // Check for Sony DADC supplier errors (audio or artwork)
    const hasSupplierError = (order) => {
        return order.audio_supplier_status === 'error' || order.artwork_supplier_status === 'error';
    };
    
    // Check if order is ready to invoice (ready_to_book and NOT yet in e-conomic)
    const isReadyToInvoice = (order) => {
        return order.invoice_status === 'ready_to_book' && !order.invoice_economic_status;
    };

    // Clear all filters (admin)
    const clearFilters = () => {
        setCustomerFilter('all');
        setStatusFilter('all');
        setProductTypeFilter('all');
        setAttentionFilters([]);
        setSearchQuery('');
    };

    const hasActiveFilters = customerFilter !== 'all' || statusFilter !== 'all' || productTypeFilter !== 'all' || orderTypeFilter !== 'all' || attentionFilters.length > 0;

    // Handle draft order deletion. Customers hit the lightweight
    // `/orders/{id}/customer` endpoint (no confirm text). Admins/super_admins
    // hit the cascade endpoint with "DELETE" as the confirm token — the
    // confirmation dialog already gates the action, so we don't force them
    // to type the title again here.
    const handleDeleteDraftOrder = async () => {
        if (!deleteOrderId) return;

        setIsDeleting(true);
        try {
            if (isAdmin) {
                await ordersApi.delete(deleteOrderId, 'DELETE');
            } else {
                await ordersApi.customerDelete(deleteOrderId);
            }
            setOrders(orders.filter(o => o.id !== deleteOrderId));
            toast.success('Draft order deleted');
            setDeleteOrderId(null);
            setDeleteOrderTitle('');
        } catch (error) {
            const message = error.response?.data?.detail || 'Failed to delete order';
            toast.error(message);
        } finally {
            setIsDeleting(false);
        }
    };


    // Toggle attention filter (admin)
    const toggleAttentionFilter = (filter) => {
        setAttentionFilters(prev => 
            prev.includes(filter) 
                ? prev.filter(f => f !== filter)
                : [...prev, filter]
        );
    };

    // Define tab categories. "On Hold" stays in the active In Progress tab —
    // it's a paused-but-active order, not cancelled — so it remains visible
    // in dashboards alongside other live work.
    const IN_PROGRESS_STATUSES = ['Quote', 'Quoted', 'Draft', 'Submitted', 'In Progress', 'On Hold', 'Shipping'];
    const COMPLETED_STATUSES = ['Delivered/Closed', 'Completed', 'Closed'];
    const CANCELLED_STATUSES = ['Cancelled'];

    // Admin-only "Draft Archive" behavior. A draft is considered archived
    // when it has had no customer/order activity for more than 14 days.
    // We anchor on the most recent of ``updated_at`` and ``created_at``
    // (updated_at is bumped by every customer edit, PATCH, file upload,
    // admin note, on-hold toggle, etc. — see routes/orders.py). This is
    // pure display filtering — the order's ``status`` stays "Draft" and
    // the customer's view is untouched.
    const DRAFT_INACTIVITY_MS = 14 * 24 * 60 * 60 * 1000;
    const isDraftArchived = (order) => {
        if (!order || order.status !== 'Draft') return false;
        const lastActivityIso = order.updated_at || order.created_at;
        if (!lastActivityIso) return false;
        const ts = new Date(lastActivityIso).getTime();
        if (Number.isNaN(ts)) return false;
        return (Date.now() - ts) > DRAFT_INACTIVITY_MS;
    };

    // Calculate tab counts
    const tabCounts = useMemo(() => {
        const inProgress = orders.filter(o => IN_PROGRESS_STATUSES.includes(o.status)).length;
        const completed = orders.filter(o => COMPLETED_STATUSES.includes(o.status)).length;
        const cancelled = orders.filter(o => CANCELLED_STATUSES.includes(o.status)).length;
        return { inProgress, completed, cancelled };
    }, [orders]);

    // Smart sorting function
    const smartSort = (ordersToSort, tab) => {
        if (tab === 'in_progress') {
            // In Progress: orders with delivery date first (earliest), then without (by updated_at)
            // Treat orders with EITHER an authoritative delivery_date OR a
            // linked dispatch-note date as "has delivery info" for sorting,
            // so dispatch-note dates also surface in the sort order.
            const effectiveDate = (o) => o.delivery_date || o.dispatch_note_date;
            const withDelivery = ordersToSort.filter(o => effectiveDate(o));
            const withoutDelivery = ordersToSort.filter(o => !effectiveDate(o));

            // Sort with delivery date by effective date ascending (earliest first)
            withDelivery.sort((a, b) => new Date(effectiveDate(a)) - new Date(effectiveDate(b)));
            
            // Sort without delivery date by updated_at descending (most recent first)
            withoutDelivery.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
            
            return [...withDelivery, ...withoutDelivery];
        } else if (tab === 'completed') {
            // Completed: sort by updated_at descending (newest first)
            return [...ordersToSort].sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
        } else {
            // Cancelled: sort by updated_at descending
            return [...ordersToSort].sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
        }
    };

    const filteredOrders = useMemo(() => {
        let result = orders;
        
        // Text search filter
        result = result.filter(order => {
            const q = searchQuery.toLowerCase();
            // Sony LEOS number lives on the linked Purchase Order
            // (`supplier_confirmation`) not the order document itself.
            // `sony_leos_number` and `sony_order_number` are kept in
            // sync on save (see routes/supplier.py) — we match either
            // so a partial LEOS reference locates the order regardless
            // of which mirror field was last written.
            const leos = (
                order.supplier_confirmation?.sony_leos_number ||
                order.supplier_confirmation?.sony_order_number ||
                ''
            ).toLowerCase();
            const matchesSearch = !searchQuery ||
                order.title.toLowerCase().includes(q) ||
                order.id.toLowerCase().includes(q) ||
                order.customer_name.toLowerCase().includes(q) ||
                (order.catalogue_number && order.catalogue_number.toLowerCase().includes(q)) ||
                (order.order_number && formatOrderNumber(order.order_number).includes(searchQuery)) ||
                (leos && leos.includes(q));
            return matchesSearch;
        });
        
        if (isAdmin) {
            // Admin view: use traditional filters
            // Customer filter — filter by ORGANIZATION ID (each company appears once,
            // not duplicated per contact user). Falls back to customer_id only for legacy
            // orders that have no organization_id.
            if (customerFilter !== 'all') {
                result = result.filter(order =>
                    order.organization_id === customerFilter ||
                    (order.organization_id == null && order.customer_id === customerFilter)
                );
            }
            
            // Status filter — one clear purpose: filter by lifecycle
            // stage. Each value maps to a well-defined bucket of order
            // statuses. Default ("all") hides completed + cancelled so
            // the list stays actionable; users click "Completed" to
            // include closed orders.
            if (statusFilter !== 'all') {
                if (statusFilter === 'quote') {
                    // Combined "Quote" pill covers both request-received
                    // (Quote) and priced-sent (Quoted) so admin can see
                    // every in-flight quote in one click.
                    result = result.filter(o => o.status === 'Quote' || o.status === 'Quoted');
                } else if (statusFilter === 'draft') {
                    // Only "live" drafts — >14-day-inactive ones live
                    // under the explicit "Draft Archive" pill (admin only).
                    result = result.filter(o => o.status === 'Draft' && !(isAdmin && isDraftArchived(o)));
                } else if (statusFilter === 'draft_archive') {
                    // Admin-only surface for stale Draft orders that have
                    // had no customer/order activity for 14+ days.
                    result = result.filter(o => isDraftArchived(o));
                } else if (statusFilter === 'submitted') {
                    result = result.filter(o => o.status === 'Submitted');
                } else if (statusFilter === 'in_progress') {
                    // "In Progress" = anything past Submitted and not yet
                    // completed/cancelled/held/shipping. Shipping gets
                    // its own pill so dispatch-phase orders are visible
                    // at a glance.
                    result = result.filter(o => ![
                        'Quote', 'Quoted', 'Draft', 'Submitted', 'On Hold',
                        'Shipping',
                        'Delivered/Closed', 'Completed', 'Closed', 'Cancelled'
                    ].includes(o.status));
                } else if (statusFilter === 'on_hold') {
                    result = result.filter(o => o.status === 'On Hold');
                } else if (statusFilter === 'shipping') {
                    // Dispatch/delivery phase — production is finished
                    // but the order isn't yet marked Completed.
                    result = result.filter(o => o.status === 'Shipping');
                } else if (statusFilter === 'completed') {
                    result = result.filter(o => COMPLETED_STATUSES.includes(o.status));
                }
            } else {
                // Default view: hide completed + cancelled so the list
                // shows only actionable orders. For admins, also hide
                // stale Drafts (Draft Archive) — they're one click away
                // via the "Draft Archive" pill.
                result = result.filter(o =>
                    !COMPLETED_STATUSES.includes(o.status) &&
                    !CANCELLED_STATUSES.includes(o.status) &&
                    !(isAdmin && isDraftArchived(o))
                );
            }
            
            // Product type filter
            if (productTypeFilter !== 'all') {
                result = result.filter(order => order.product_type === productTypeFilter);
            }
            
            // Order type filter (new / reorder)
            if (orderTypeFilter !== 'all') {
                if (orderTypeFilter === 'new') {
                    result = result.filter(o => o.order_type !== 'reorder');
                } else if (orderTypeFilter === 'reorder') {
                    result = result.filter(o => o.order_type === 'reorder');
                }
            }
            
            // Attention filters (OR logic)
            if (attentionFilters.length > 0) {
                result = result.filter(order => {
                    return attentionFilters.some(filter => {
                        if (filter === 'artwork') return hasArtworkIssue(order);
                        if (filter === 'audio') return hasMissingAudio(order);
                        if (filter === 'ncb') return hasNCBIssue(order);
                        if (filter === 'supplier') return hasSupplierIssue(order);
                        if (filter === 'supplier_error') return hasSupplierError(order);
                        if (filter === 'ready_to_invoice') return isReadyToInvoice(order);
                        if (filter === 'md5') return !!order.has_md5_issue;
                        if (filter === 'low_margin') return !!order.has_low_margin;
                        if (filter === 'ready_for_production') {
                            // Backend computes the definitive list (joins
                            // orders + repro_jobs + supplier_confirmations)
                            // and returns the order-id set in
                            // actionsSummary.ready_for_production_order_ids.
                            // We simply filter the loaded orders by that set.
                            const ids = actionsSummary?.ready_for_production_order_ids || [];
                            return ids.includes(order.id);
                        }
                        if (filter === 'supplier_validation_mismatch') {
                            // Backend flags orders whose
                            // artwork_supplier_validation.validation_status
                            // == "mismatch" (Sony vs FLOW). Auto-clears when
                            // the mismatch is resolved or the order is
                            // archived. IDs come from
                            // actionsSummary.supplier_validation_mismatch_order_ids.
                            const ids = actionsSummary?.supplier_validation_mismatch_order_ids || [];
                            return ids.includes(order.id);
                        }
                        if (filter === 'master_not_ready') {
                            // Backend flags every active order whose master
                            // is NOT production-ready: no master uploaded,
                            // missing/mismatched MD5, pending sidecar, or
                            // stalled chunked upload. Superset of the MD5
                            // bucket. Admin override via
                            // audio_supplier_status='received' excludes.
                            const ids = actionsSummary?.master_not_ready_order_ids || [];
                            return ids.includes(order.id);
                        }
                        if (filter === 'missing_master') {
                            // Required master files actually missing — no
                            // master uploaded, a rejected master awaiting a
                            // customer re-upload, or an incomplete/stalled
                            // upload. Waiting for Customer. Disjoint from
                            // `master_verification_pending` so the two never
                            // double-count.
                            const ids = actionsSummary?.missing_master_order_ids || [];
                            return ids.includes(order.id);
                        }
                        if (filter === 'master_verification_pending') {
                            // Master HAS been uploaded but MD5 verification or
                            // manifest validation is still pending / mismatched
                            // / needs internal action. Internal Action Today.
                            // Auto-clears once verification completes. IDs come
                            // from actionsSummary.master_verification_pending_order_ids.
                            const ids = actionsSummary?.master_verification_pending_order_ids || [];
                            return ids.includes(order.id);
                        }
                        if (filter === 'master_ready_not_sent') {
                            // Production-ready masters that haven't yet been
                            // sent to Sony (audio_supplier_status is null /
                            // empty). Auto-clears when admin flips it to
                            // awaiting/received. IDs come from
                            // actionsSummary.master_ready_not_sent_order_ids.
                            const ids = actionsSummary?.master_ready_not_sent_order_ids || [];
                            return ids.includes(order.id);
                        }
                        if (filter === 'supplier_confirmation_needs_verification') {
                            // Production supplier_confirmations that Inbound
                            // Hub just changed. Auto-clears the moment the
                            // admin approves the confirmation (see
                            // ``needs_verification`` handling in supplier.py).
                            const ids = actionsSummary?.supplier_confirmation_needs_verification_order_ids || [];
                            return ids.includes(order.id);
                        }
                        if (filter === 'tp_confirmation_needs_verification') {
                            // Test Press supplier_confirmations that Inbound
                            // Hub has just linked/updated. Kept independent of
                            // the production bucket — approving one variant
                            // never clears the other.
                            const ids = actionsSummary?.tp_confirmation_needs_verification_order_ids || [];
                            return ids.includes(order.id);
                        }
                        if (filter === 'dispatch_needs_verification') {
                            // Production supplier_confirmations whose dispatch
                            // block was auto-populated by Inbound Hub and not
                            // yet approved by an admin. Cleared by "Approve &
                            // Send dispatch notification".
                            const ids = actionsSummary?.dispatch_needs_verification_order_ids || [];
                            return ids.includes(order.id);
                        }
                        if (filter === 'tp_dispatch_needs_verification') {
                            // Same mechanism as above but on the TP variant.
                            // Kept as a separate list so the TP review action
                            // is disjoint from the production review action.
                            const ids = actionsSummary?.tp_dispatch_needs_verification_order_ids || [];
                            return ids.includes(order.id);
                        }
                        if (filter === 'waiting_for_supplier') {
                            // Production POs sent to Sony where no Order
                            // Confirmation has been received yet (neither via
                            // Inbound Hub nor manual entry). Auto-clears the
                            // moment OC data lands. IDs come from
                            // actionsSummary.waiting_for_supplier_order_ids.
                            const ids = actionsSummary?.waiting_for_supplier_order_ids || [];
                            return ids.includes(order.id);
                        }
                        if (filter === 'orders_awaiting_sony_placement') {
                            // Active production orders (incl. reorders)
                            // where the Sony Production Order has not
                            // yet been sent (``po_sent_at`` unset on
                            // the production supplier_confirmation, or
                            // the SC doesn't exist yet). Auto-clears
                            // when Supplier → Production Order →
                            // "Approve & Send PO" fires.
                            const ids = actionsSummary?.orders_awaiting_sony_placement_order_ids || [];
                            return ids.includes(order.id);
                        }
                        if (filter === 'test_press_awaiting_customer') {
                            // Test Press delivered to customer, awaiting
                            // their approve/reject. Auto-clears once the
                            // customer decides. IDs come from
                            // actionsSummary.test_press_awaiting_customer_order_ids.
                            const ids = actionsSummary?.test_press_awaiting_customer_order_ids || [];
                            return ids.includes(order.id);
                        }
                        if (filter === 'tp_order_to_sony_pending') {
                            // Internal action — customer purchased TPs
                            // but admin hasn't sent the Sony PO yet.
                            // Auto-clears once state advances to "ordered".
                            const ids = actionsSummary?.tp_order_to_sony_pending_order_ids || [];
                            return ids.includes(order.id);
                        }
                        if (filter === 'tp_send_feedback_to_sony') {
                            // Internal action — customer rejected the TP
                            // and admin still needs to forward feedback
                            // to the pressing plant. Auto-clears once
                            // the TP advances to "awaiting_feedback".
                            const ids = actionsSummary?.tp_send_feedback_to_sony_order_ids || [];
                            return ids.includes(order.id);
                        }
                        if (filter === 'tp_awaiting_sony_response') {
                            // Waiting for Supplier — TP feedback has
                            // been emailed to Sony and we're waiting
                            // for their response. Auto-clears when
                            // the admin applies a Phase 4 branch.
                            const ids = actionsSummary?.tp_awaiting_sony_response_order_ids || [];
                            return ids.includes(order.id);
                        }
                        if (filter === 'rfq_to_send') {
                            // Supplier RFQ not yet sent, but the BOM has
                            // at least one line without a confirmed
                            // unit price. Backend list sourced from
                            // actionsSummary.rfq_to_send_order_ids.
                            const ids = actionsSummary?.rfq_to_send_order_ids || [];
                            return ids.includes(order.id);
                        }
                        if (filter === 'rfq_awaiting') {
                            // Supplier RFQ has been sent successfully
                            // and no production PO has gone out yet.
                            // Overdue orders ARE included here — the
                            // bucket is a superset of rfq_overdue.
                            const ids = actionsSummary?.rfq_awaiting_order_ids || [];
                            return ids.includes(order.id);
                        }
                        if (filter === 'rfq_overdue') {
                            // Subset of rfq_awaiting whose earliest
                            // successful send is older than the
                            // configured business-day threshold.
                            // Resending does NOT reset the timer.
                            const ids = actionsSummary?.rfq_overdue_order_ids || [];
                            return ids.includes(order.id);
                        }
                        return false;
                    });
                });
            }
            
            // Default sort for admin: by selected column. Falls back to created_at when no preference.
            const sortAccessor = (order, key) => {
                switch (key) {
                    case 'order_number':
                        return order.order_number ?? 0;
                    case 'product_type':
                        return (order.product_type || 'vinyl').toLowerCase();
                    case 'catalogue_number':
                        return (order.catalogue_number || '').toLowerCase();
                    case 'title':
                        return (order.title || '').toLowerCase();
                    case 'customer':
                        return (order.organization_name || order.customer_name || '').toLowerCase();
                    case 'artwork':
                        // Combine review status + workflow status so badge ordering is intuitive
                        return (order.artwork_review_status || order.artwork_status || '').toLowerCase();
                    case 'master':
                        // Received < Uploaded < Awaiting < Missing — represent numerically so direction is consistent
                        if (order.has_audio_master) {
                            const sup = (order.audio_supplier_status || '').toLowerCase();
                            if (sup === 'received') return 4;
                            if (sup === 'uploaded') return 3;
                            if (sup === 'awaiting') return 2;
                            return 1; // received but no supplier status yet
                        }
                        return 0;
                    case 'status':
                        return (order.status || '').toLowerCase();
                    case 'delivery':
                        return order.delivery_date ? new Date(order.delivery_date).getTime() : null;
                    default:
                        return new Date(order.created_at).getTime();
                }
            };
            const dirMul = sortDir === 'asc' ? 1 : -1;
            result = [...result].sort((a, b) => {
                const va = sortAccessor(a, sortBy);
                const vb = sortAccessor(b, sortBy);
                // Null values always sort to the bottom regardless of direction
                if (va == null && vb == null) return 0;
                if (va == null) return 1;
                if (vb == null) return -1;
                if (va < vb) return -1 * dirMul;
                if (va > vb) return 1 * dirMul;
                // Stable tie-breaker: newest first
                return new Date(b.created_at) - new Date(a.created_at);
            });
        } else {
            // Customer view: quick filter (from Action Center clicks)
            // takes precedence over the status pills. When set, we
            // ignore activeTab so the table shows exactly the sub-slice
            // the customer asked for. See `setQuickFilter` for values.
            if (quickFilter === 'artwork-approval') {
                result = result.filter(
                    (o) => o.artwork_status === 'Awaiting_Approval'
                        && o.artwork_review_status !== 'rejected',
                );
            } else if (quickFilter === 'artwork-revision') {
                result = result.filter((o) => o.artwork_review_status === 'rejected');
            } else if (quickFilter === 'missing-master') {
                const inProg = ['Submitted', 'In Production', 'in_production', 'Shipping'];
                result = result.filter(
                    (o) => (o.master_status === 'Missing' || o.master_status === 'missing')
                        && inProg.includes(o.status),
                );
            } else if (quickFilter === 'artwork-required') {
                // Customer Action Center → "N orders need artwork".
                // Mirrors the bucket's own filter so counts line up.
                const inProg = ['Submitted', 'In Production', 'in_production', 'Shipping'];
                result = result.filter(
                    (o) => inProg.includes(o.status)
                        && !o.artwork_status
                        && o.order_type !== 'reorder'
                        && !o.reusable_artwork,
                );
            } else if (quickFilter === 'quote') {
                // Customer Action Center → "N quotes waiting for your
                // approval". Must mirror ``CustomerActionCenter``'s
                // own predicate exactly (status === 'Quoted' AND the
                // active snapshot is a priced, still-sent version
                // the customer has NOT yet approved/rejected). Using
                // ``status === 'Quote'`` alone would include quote
                // REQUESTS that admin hasn't priced yet — not
                // actionable from the customer side.
                result = result.filter((o) => {
                    if (o.status !== 'Quoted') return false;
                    const versions = Array.isArray(o.quote_versions) ? o.quote_versions : [];
                    if (versions.length === 0) return false;
                    const current = versions.find((v) => v.version === o.quote_current_version)
                        || versions[versions.length - 1];
                    if (!current) return false;
                    if (current.status && current.status !== 'sent') return false;
                    const total = Number(current.total);
                    const lines = Array.isArray(current.lines) ? current.lines : [];
                    return lines.length > 0 && Number.isFinite(total) && total > 0;
                });
            } else if (quickFilter === 'test-press-review') {
                // Customer Action Center → "N test presses ready for
                // review". Must mirror the bucket's own predicate
                // (`test_press_status === 'arrived'`) exactly — the
                // single-order case already deep-links straight into
                // the Test Press card, this branch handles the N > 1
                // drill-down. ``arrived`` is the only state the TP
                // state machine parks in while awaiting the customer's
                // approve/reject; ``approved`` / ``rejected`` are
                // terminal so this list auto-trims the moment the
                // customer decides.
                result = result.filter((o) => o.test_press_status === 'arrived');
            } else if (activeTab === 'in_progress') {
                result = result.filter(o => IN_PROGRESS_STATUSES.includes(o.status) && o.status !== 'Draft');
            } else if (activeTab === 'completed') {
                result = result.filter(o => COMPLETED_STATUSES.includes(o.status));
            } else if (activeTab === 'cancelled') {
                result = result.filter(o => CANCELLED_STATUSES.includes(o.status));
            } else if (activeTab === 'draft') {
                // Admin: exclude archived drafts (14+ days inactive).
                // Customers: unchanged.
                result = result.filter(o => o.status === 'Draft' && !(isAdmin && isDraftArchived(o)));
            }
            // 'all' → no status filter

            // Apply smart sorting
            result = smartSort(result, activeTab);
        }
        
        return result;
    }, [orders, searchQuery, customerFilter, statusFilter, productTypeFilter, orderTypeFilter, attentionFilters, actionsSummary, isAdmin, activeTab, quickFilter, COMPLETED_STATUSES, CANCELLED_STATUSES, sortBy, sortDir]);

    // Calculate metrics
    const totalOrders = orders.length;
    const activeOrders = orders.filter(o => !COMPLETED_STATUSES.includes(o.status) && !CANCELLED_STATUSES.includes(o.status)).length;
    const draftOrders = orders.filter(o => o.status === 'Draft' && !(isAdmin && isDraftArchived(o))).length;
    const inProgressOrders = orders.filter(o => !['Draft', 'Delivered/Closed', 'Completed', 'Closed', 'Cancelled'].includes(o.status)).length;
    const completedOrders = orders.filter(o => COMPLETED_STATUSES.includes(o.status)).length;
    const totalPurchaseAmount = purchases.reduce((sum, p) => sum + p.amount, 0);
    
    // Calculate Open Order Value - sum of Sales Document totals for orders NOT yet
    // closed out in finance.
    //
    // An order counts as "open" when:
    //   - it is NOT cancelled
    //   - AND its invoice_status is NOT one of the "closed-out" states
    //     ('sent', 'booked' or 'paid'). Once an invoice has been sent to the
    //     customer the order is no longer in the "open to be invoiced" pipeline
    //     — even if payment hasn't landed yet. Only draft invoices (still being
    //     prepared) keep the order open.
    const CLOSED_INVOICE_STATUSES = ['sent', 'booked', 'paid']; // kept for reference (used indirectly via invoice_state below)
    // Two related but distinct financial metrics computed together
    // (share the same iteration + FX conversion). Returns:
    //   • totalActive:      all non-Draft, non-Cancelled orders — full
    //     production pipeline value including already-invoiced work.
    //   • openUninvoiced:   the subset that has NO invoice presence yet
    //     (no local sales document, no e-conomic draft, no booked invoice).
    //     This is the amount still to be invoiced.
    // Every detail row carries `invoice_state` + `inclusion_reason` so the
    // breakdown modal can explain why an order is in/out.
    const { totalActiveOrderValue, openUninvoicedOrderValue } = useMemo(() => {
        // FX rate for legacy fallback path (invoices in DKK/EUR only).
        const DEFAULT_DKK_PER_EUR = 7.46;

        const active = { dkk: 0, eur: 0, count: 0, details: [] };
        const uninvoiced = { dkk: 0, eur: 0, count: 0, details: [] };

        orders.forEach(o => {
            // Skip drafts + cancelled — never part of either metric.
            if (o.status === 'Draft' || o.status === 'Cancelled') return;

            // Determine invoice presence.
            //   invoice_status         : 'draft' (local sales doc), 'booked' etc.
            //   invoice_economic_status: 'local_draft' | 'draft_in_economic' | 'booked_in_economic'
            const iStatus = o.invoice_status || null;
            const eStatus = o.invoice_economic_status || null;

            let invoice_state;      // machine key
            let inclusion_reason;   // human-readable
            let isUninvoiced = false;

            if (eStatus === 'booked_in_economic' || iStatus === 'booked' || iStatus === 'paid') {
                invoice_state = 'booked';
                inclusion_reason = 'Booked in e-conomic — tracked in Finance (outstanding/paid)';
            } else if (eStatus === 'draft_in_economic') {
                invoice_state = 'economic_draft';
                inclusion_reason = 'E-conomic draft invoice created';
            } else if (eStatus === 'local_draft' || iStatus === 'draft' || iStatus === 'ready_to_book') {
                invoice_state = 'local_draft';
                inclusion_reason = 'Local draft sales document created';
            } else {
                invoice_state = 'none';
                inclusion_reason = 'No sales document — still to be invoiced';
                isUninvoiced = true;
            }

            // Amount resolution — prefer sales-document total, fall back to
            // frozen order totals on the order document.
            let dkk = 0;
            let eur = 0;
            let source = 'order_totals';
            if (o.invoice_total) {
                if (o.invoice_currency === 'DKK') {
                    dkk = o.invoice_total;
                    eur = o.invoice_total / DEFAULT_DKK_PER_EUR;
                } else if (o.invoice_currency === 'EUR') {
                    eur = o.invoice_total;
                    dkk = o.invoice_total * DEFAULT_DKK_PER_EUR;
                }
                source = 'invoice';
            } else {
                dkk = o.order_total_local || 0;
                eur = o.order_total_eur || 0;
                source = 'order_totals';
            }

            const detail = {
                id: o.id,
                order_number: o.order_number,
                title: o.title,
                status: o.status,
                dkk,
                eur,
                source,
                invoice_state,
                inclusion_reason,
                // Gross margin — admin-only, populated by the backend
                // (`_compute_order_margins_map`). null when the order has
                // no cost yet (drafts / uncosted) or no revenue.
                gross_profit_eur: (o.gross_profit_eur ?? null),
                gross_margin_pct: (o.gross_margin_pct ?? null),
                // Human-readable explanation when the margin can't be
                // computed (e.g. "Missing purchase cost"). Rendered in
                // place of "—" so admins immediately see WHICH datum is
                // missing.
                gross_margin_reason: (o.gross_margin_reason ?? null),
            };

            // Total Active — every qualifying order.
            active.dkk += dkk;
            active.eur += eur;
            active.count += 1;
            active.details.push(detail);

            // Open Uninvoiced — only when there is no invoice presence.
            if (isUninvoiced) {
                uninvoiced.dkk += dkk;
                uninvoiced.eur += eur;
                uninvoiced.count += 1;
                uninvoiced.details.push(detail);
            }
        });

        // Sort descending by DKK amount.
        active.details.sort((a, b) => (b.dkk || 0) - (a.dkk || 0));
        uninvoiced.details.sort((a, b) => (b.dkk || 0) - (a.dkk || 0));

        return {
            totalActiveOrderValue: active,
            openUninvoicedOrderValue: uninvoiced,
        };
    }, [orders]);

    if (loading) {
        return (
            <div className="min-h-screen bg-background">
                <Header />
                <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
                    <LoadingSpinner size="lg" />
                </div>
            </div>
        );
    }

    // ==================== NEW USER LANDING PAGE ====================
    // Show pricing-focused landing for customers with no orders (if enabled in settings)
    if (!isAdmin && orders.length === 0 && platformSettings.show_new_customer_landing) {
        const lp = platformSettings.landing_page || {};
        const previewCard = lp.preview_card || {};
        const trustPoints = lp.trust_points || ['Instant pricing', 'No hidden fees', 'Professional production'];
        
        return (
            <div className="min-h-screen bg-background" data-testid="dashboard-page">
                <Header focusMode={true} />
                
                <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
                    <div className="grid lg:grid-cols-5 gap-12 lg:gap-16 items-center">
                        {/* Left Side - Copy & CTA (60%) */}
                        <div className="lg:col-span-3">
                            <h1 
                                className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4 leading-tight" 
                                style={{ fontFamily: 'Inter, sans-serif' }}
                            >
                                {lp.headline || 'Know your vinyl production cost — before you commit.'}
                            </h1>
                            <p className="text-lg sm:text-xl text-slate-600 mb-8">
                                {lp.subtext || 'Adjust format and quantity — your total updates instantly.'}
                            </p>

                            <Button 
                                onClick={handleQuickNewOrder}
                                disabled={isCreating}
                                size="lg"
                                className="bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-6 text-lg h-auto mb-4"
                                data-testid="get-instant-quote-btn"
                            >
                                {isCreating ? (
                                    <>
                                        <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                                        Creating...
                                    </>
                                ) : (
                                    lp.button_text || 'Get Instant Quote'
                                )}
                            </Button>

                            <p className="text-sm text-slate-500">
                                {lp.tagline || 'No commitment. No hidden costs.'}
                            </p>
                        </div>

                        {/* Right Side - Pricing Preview Mock (40%) */}
                        <div className="lg:col-span-2">
                            <div className="bg-white rounded-xl border border-border shadow-premium-lg p-6 max-w-sm mx-auto lg:mx-0">
                                {/* Product Summary */}
                                <div className="mb-5">
                                    <div className="flex items-center gap-2 mb-3">
                                        <div className="w-10 h-10 rounded-lg bg-slate-900 flex items-center justify-center">
                                            <Package className="h-5 w-5 text-white" />
                                        </div>
                                        <div>
                                            <p className="font-semibold text-slate-900">{previewCard.product_name || '12" Black Vinyl'}</p>
                                            <p className="text-sm text-slate-500">{previewCard.product_specs || '300 units • Standard Sleeve'}</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Price Display */}
                                <div className="border-t border-b border-slate-100 py-4 mb-5">
                                    <div className="flex items-baseline justify-between">
                                        <span className="text-sm text-slate-500">{t('wizard.per_unit', 'Per unit')}</span>
                                        <span className="text-2xl font-bold text-slate-900">{previewCard.per_unit_price || '€6.95'}</span>
                                    </div>
                                    <div className="flex items-baseline justify-between mt-1">
                                        <span className="text-sm text-slate-500">{t('wizard.total_estimate', 'Total estimate')}</span>
                                        <span className="text-lg font-semibold text-slate-700">{previewCard.total_price || '€2,085'}</span>
                                    </div>
                                </div>

                                {/* Trust Points */}
                                <div className="space-y-2">
                                    {trustPoints.map((point, index) => (
                                        <div key={index} className="flex items-center gap-2 text-sm text-slate-600">
                                            <CheckCircle className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                                            <span>{point}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </main>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background" data-testid="dashboard-page">
            <Header />
            
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Page Header — for admin the "All Orders" h1 is redundant
                    (they are already inside the Orders section) so we skip it
                    and just render the subtitle. Customer keeps the full header. */}
                <div className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 ${isAdmin ? 'mb-4' : 'mb-8'}`}>
                    <div>
                        {!isAdmin && (
                            <h1 className="text-2xl font-bold text-slate-900">
                                {t('dashboard.myorders', 'My Orders')}
                            </h1>
                        )}
                        <p className={isAdmin ? 'text-2xl font-bold text-slate-900' : 'text-sm text-muted-foreground mt-1'}>
                            {isAdmin 
                                ? t('dashboard.admin.subtitle', 'Manage all customer orders and purchases') 
                                : t('dashboard.customer.subtitle', 'View and manage your orders')
                            }
                        </p>
                    </div>

                    {/* New Order Button - Different behavior based on context */}
                    {!isAdmin && catalogueEntries.length > 0 ? (
                        // Customer with catalogue entries: Show reorder modal
                        <Dialog open={isCreateDialogOpen} onOpenChange={(open) => { setIsCreateDialogOpen(open); if (!open) resetCreateForm(); }}>
                            <div className="flex gap-2">
                                <Button 
                                    onClick={handleQuickNewOrder}
                                    disabled={isCreating}
                                    className="bg-slate-900 hover:bg-slate-800 text-white btn-active"
                                    data-testid="create-order-button"
                                >
                                    <Plus className="h-4 w-4 mr-2" />
                                    {isCreating ? 'Creating...' : t('dashboard.neworder', 'New Order')}
                                </Button>
                                <DialogTrigger asChild>
                                    <Button 
                                        variant="outline"
                                        className="border-purple-200 text-purple-600 hover:bg-purple-50"
                                        data-testid="reorder-button"
                                        onClick={() => setOrderType('reorder')}
                                    >
                                        <RefreshCw className="h-4 w-4 mr-2" />
                                        Reorder
                                    </Button>
                                </DialogTrigger>
                            </div>
                            <DialogContent className="sm:max-w-lg">
                                <DialogHeader>
                                    <DialogTitle style={{ fontFamily: 'Inter, sans-serif' }}>
                                        Reorder from Catalogue
                                    </DialogTitle>
                                    <DialogDescription>
                                        Quick reorder using an existing catalogue entry
                                    </DialogDescription>
                                </DialogHeader>
                                
                                <form onSubmit={handleCreateOrder} className="space-y-4 mt-2">
                                    {/* Catalogue Entry Selector */}
                                    <div className="space-y-2">
                                        <Label>Select Catalogue Entry *</Label>
                                        <Popover open={catalogueSearchOpen} onOpenChange={setCatalogueSearchOpen}>
                                            <PopoverTrigger asChild>
                                                <Button
                                                    variant="outline"
                                                    role="combobox"
                                                    className="w-full justify-between h-10"
                                                    data-testid="catalogue-selector"
                                                >
                                                    {selectedCatalogueEntry 
                                                        ? `${selectedCatalogueEntry.catalogue_number} - ${selectedCatalogueEntry.title || 'Untitled'}`
                                                        : "Select catalogue entry..."
                                                    }
                                                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                                </Button>
                                            </PopoverTrigger>
                                            <PopoverContent className="w-[400px] p-0">
                                                <Command>
                                                    <CommandInput placeholder="Search catalogue..." />
                                                    <CommandList>
                                                        <CommandEmpty>{t('dashboard.no_catalogue_entries', 'No catalogue entries found.')}</CommandEmpty>
                                                        <CommandGroup>
                                                            {catalogueEntries.map((entry) => (
                                                                <CommandItem
                                                                    key={entry.id}
                                                                    value={`${entry.catalogue_number} ${entry.title} ${entry.artist}`}
                                                                    onSelect={() => {
                                                                        setSelectedCatalogueEntry(entry);
                                                                        setCatalogueSearchOpen(false);
                                                                    }}
                                                                >
                                                                    <div className="flex flex-col">
                                                                        <span className="font-medium">{entry.catalogue_number}</span>
                                                                        <span className="text-xs text-slate-500">
                                                                            {entry.artist && `${entry.artist} - `}{entry.title || 'Untitled'}
                                                                        </span>
                                                                    </div>
                                                                </CommandItem>
                                                            ))}
                                                        </CommandGroup>
                                                    </CommandList>
                                                </Command>
                                            </PopoverContent>
                                        </Popover>
                                    </div>
                                    
                                    {/* Show selected entry details */}
                                    {selectedCatalogueEntry && (() => {
                                        // Match the backend's canonical resolution: media_type
                                        // (vinyl / cd / dvd / bluray) is the source of truth
                                        // for the product family. The vinyl `format` field
                                        // (7"/10"/12") is only relevant when the media is
                                        // vinyl — do NOT show "Format: —" for CD/DVD/BD.
                                        const rawMedia = (selectedCatalogueEntry.media_type || '').toLowerCase();
                                        const productLabel = ({
                                            vinyl: 'Vinyl', cd: 'CD', dvd: 'DVD',
                                            bluray: 'Blu-ray', blu_ray: 'Blu-ray', 'blu-ray': 'Blu-ray',
                                        })[rawMedia] || 'Vinyl';
                                        const isVinyl = productLabel === 'Vinyl';
                                        const fmt = selectedCatalogueEntry.format;
                                        return (
                                            <div className="p-3 bg-background rounded-lg space-y-2">
                                                <div className="flex justify-between text-sm">
                                                    <span className="text-slate-500">{t('artwork_share.artist_prefix', 'Artist:')}</span>
                                                    <span className="font-medium">{selectedCatalogueEntry.artist || '—'}</span>
                                                </div>
                                                <div className="flex justify-between text-sm">
                                                    <span className="text-slate-500">{t('dashboard.title_prefix', 'Title:')}</span>
                                                    <span className="font-medium">{selectedCatalogueEntry.title || '—'}</span>
                                                </div>
                                                <div className="flex justify-between text-sm">
                                                    <span className="text-slate-500">{t('dashboard.product_prefix', 'Product:')}</span>
                                                    <span className="font-medium" data-testid="reorder-product-label">
                                                        {productLabel}
                                                        {isVinyl && fmt
                                                            ? ` · ${fmt}${/^\d+$/.test(fmt) ? '"' : ''}`
                                                            : ''}
                                                    </span>
                                                </div>
                                            </div>
                                        );
                                    })()}
                                    
                                    {/* Quantity — free numeric input constrained by the
                                        effective reorder policy (entry override → family
                                        default). The Select-with-fixed-values input hid
                                        the fact that DVD/CD/BD have lower minimums than
                                        the vinyl 250 default. */}
                                    <div className="space-y-2">
                                        <Label htmlFor="reorderQuantity">{t('dashboard.quantity_to_produce', 'Quantity to produce')}</Label>
                                        <Input
                                            id="reorderQuantity"
                                            type="number"
                                            min={selectedReorderPolicy?.min_qty ?? 1}
                                            step={1}
                                            value={reorderQuantity}
                                            onChange={(e) => setReorderQuantity(e.target.value)}
                                            onFocus={(e) => e.target.select()}
                                            data-testid="quantity-selector"
                                        />
                                        {selectedReorderPolicy && (
                                            <p className="text-xs text-slate-500">
                                                Minimum{' '}
                                                <span className="font-medium">
                                                    {selectedReorderPolicy.product_family?.toUpperCase()}
                                                </span>{' '}
                                                reorder is{' '}
                                                <strong>{selectedReorderPolicy.min_qty} units</strong>
                                                {selectedReorderPolicy.source === 'entry_override'
                                                    ? ' (override for this title).'
                                                    : ' (family default).'}
                                            </p>
                                        )}
                                        {selectedReorderPolicy
                                          && parseInt(reorderQuantity, 10) > 0
                                          && parseInt(reorderQuantity, 10) < selectedReorderPolicy.min_qty && (
                                            <p className="text-xs text-red-600" data-testid="reorder-qty-min-error">
                                                Quantity must be at least {selectedReorderPolicy.min_qty} units.
                                            </p>
                                        )}
                                    </div>
                                    
                                    {/* Note: Test Pressings not available for reorders */}
                                    <p className="text-xs text-slate-500 bg-background p-2 rounded">
                                        Reorders use your existing master — no test pressings needed.
                                    </p>

                                    {/* Live estimated price preview — shared component
                                        with the Catalogue Library detail modal so both
                                        reorder entry points always show the same number. */}
                                    {selectedCatalogueEntry && (
                                        <ReorderQuoteCard
                                            quote={reorderQuote}
                                            loading={reorderQuoteLoading}
                                        />
                                    )}

                                    <div className="flex justify-end gap-3 pt-4 border-t">
                                        <Button 
                                            type="button" 
                                            variant="outline" 
                                            onClick={() => setIsCreateDialogOpen(false)}
                                        >
                                            Cancel
                                        </Button>
                                        <Button 
                                            type="submit" 
                                            disabled={isCreating || !selectedCatalogueEntry}
                                            className="bg-purple-600 hover:bg-purple-700"
                                            data-testid="create-reorder-btn"
                                        >
                                            {isCreating ? 'Creating...' : 'Create Reorder'}
                                        </Button>
                                    </div>
                                </form>
                            </DialogContent>
                        </Dialog>
                    ) : (!isAdmin) ? (
                        // Customer without catalogue: Direct new order button.
                        // Admins never create orders manually — orders come from
                        // customers, campaigns, integrations, or sales.
                        <Button 
                            onClick={handleQuickNewOrder}
                            disabled={isCreating}
                            className="bg-slate-900 hover:bg-slate-800 text-white btn-active"
                            data-testid="create-order-button"
                        >
                            <Plus className="h-4 w-4 mr-2" />
                            {isCreating ? 'Creating...' : t('dashboard.neworder', 'New Order')}
                        </Button>
                    ) : null}
                </div>

                {/* Order Health + Today's Progress — compact 2-col row.
                    Health = overall workflow state, Progress = today's
                    momentum. Both keep Needs Attention high on the page. */}
                {isAdmin && actionsSummary && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-4">
                        <OrderHealthScore actionsSummary={actionsSummary} />
                        <TodayProgress
                            userId={user?.id}
                            totalActions={actionsSummary.total_actions || 0}
                        />
                    </div>
                )}

                {/* Needs Attention Section - Admin only. Grouped into
                    four semantic buckets so admins can scan by "who
                    needs to act" rather than eyeballing a wall of
                    same-priority chips:
                        Internal Action - today
                        Waiting for Customer
                        Follow-up
                        Admin / Account Tasks
                    Backend counters can overlap (e.g. `master_not_ready`
                    is a superset of `missing_master` + `md5_issues`);
                    the redesigned UI ONLY reads the disjoint counters
                    so the total badge and the per-column sums can't drift. */}
                {isAdmin && actionsSummary && actionsSummary.total_actions > 0 && (() => {
                    // ``orders`` (outer) is the loaded orders array.
                    // ``orderCounts`` is the per-bucket count dict from the
                    // /admin/actions-summary endpoint — keep names distinct
                    // so the NCB reminder menu can look up full order docs
                    // by ID from the loaded array.
                    const loadedOrders = orders;
                    const orderCounts = actionsSummary.orders || {};
                    const finance = actionsSummary.finance || {};
                    const users = actionsSummary.users || {};

                    // Returns JSX (not a component) - avoids the
                    // react/no-unstable-nested-components lint rule.
                    // `resolveScope` opts a card into the Needs-Attention
                    // resolve popover (approve / mark_duplicate / dismiss /
                    // snooze). Cards that pass it render as a div with the
                    // primary click target on the content row and the
                    // resolve trigger as a sibling button — nesting real
                    // <button> elements would be invalid HTML.
                    const renderCard = ({ key, count, title, subtitle, dot, onClick, testId, titleFirst, resolveScope, subtitleAsTooltip, sideMenu }) => {
                        const showInlineSubtitle = subtitle && !subtitleAsTooltip;
                        const tooltipText = subtitle && subtitleAsTooltip ? subtitle : undefined;
                        const hasSideSlot = !!resolveScope || !!sideMenu;
                        const contentRow = (
                            <>
                                <div className="flex items-center gap-2 w-full">
                                    <div className={`h-2 w-2 rounded-full bg-${dot || 'amber'}-500 flex-shrink-0`}></div>
                                    <span className="text-slate-700 flex-1">
                                        {titleFirst
                                            ? <><span className="font-medium">{titleFirst}</span> {count} {title}</>
                                            : <><span className="font-medium">{count}</span> {title}</>}
                                    </span>
                                    {!hasSideSlot && <ChevronRight className="h-4 w-4 text-slate-400 flex-shrink-0" />}
                                </div>
                                {showInlineSubtitle && <span className="text-[11px] text-slate-500 pl-4">{subtitle}</span>}
                            </>
                        );

                        if (hasSideSlot) {
                            return (
                                <div
                                    key={key || testId}
                                    className="w-full flex items-stretch gap-1 bg-white border border-slate-200 rounded-lg hover:border-slate-400 transition-colors"
                                    data-testid={testId}
                                    title={tooltipText}
                                >
                                    <button
                                        onClick={onClick}
                                        className="flex-1 flex flex-col items-start gap-1 px-3 py-2 hover:bg-slate-50 rounded-l-lg text-sm text-left"
                                        data-testid={`${testId}-open`}
                                    >
                                        {contentRow}
                                    </button>
                                    <div className="flex items-center pr-2 border-l border-slate-100">
                                        {resolveScope ? (
                                            <NeedsAttentionResolvePanel
                                                scope={resolveScope}
                                                onResolved={fetchActionsSummary}
                                            />
                                        ) : sideMenu}
                                    </div>
                                </div>
                            );
                        }

                        return (
                            <button
                                key={key || testId}
                                onClick={onClick}
                                className="w-full flex flex-col items-start gap-1 px-3 py-2 bg-white border border-slate-200 rounded-lg hover:border-slate-400 hover:bg-slate-50 transition-colors text-sm text-left"
                                data-testid={testId}
                                title={tooltipText}
                            >
                                {contentRow}
                            </button>
                        );
                    };

                    const renderColumn = (colTitle, colColour, items, testId) => (
                        items.length > 0 ? (
                            <div key={testId} className="flex flex-col gap-2" data-testid={testId}>
                                <div className="flex items-center gap-2 px-1">
                                    <div className={`h-2 w-2 rounded-full bg-${colColour}-500`}></div>
                                    <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-700">{colTitle}</h4>
                                    <Badge variant="outline" className="ml-1 text-[10px] px-1.5 py-0 h-4">{items.length}</Badge>
                                </div>
                                <div className="flex flex-col gap-2">{items}</div>
                            </div>
                        ) : null
                    );

                    // Consistent Needs-Attention navigation: when a card
                    // represents exactly ONE actionable item, open that
                    // order directly with the relevant section expanded
                    // (the order-detail page auto-expands the section whose
                    // id matches the URL hash). With multiple items we fall
                    // through to the caller's existing filtered-list view.
                    const singleDeepLink = (ids, hash) => {
                        if (Array.isArray(ids) && ids.length === 1) {
                            navigate(`/orders/${ids[0]}${hash || ''}`);
                            return true;
                        }
                        return false;
                    };

                    const internal = [];
                    if (orderCounts.supplier_confirmation_needs_verification > 0) internal.push(renderCard({
                        key: 'scv', testId: 'attention-supplier-confirmation-needs-verification', dot: 'amber',
                        count: orderCounts.supplier_confirmation_needs_verification, titleFirst: 'Verify',
                        title: `production confirmation${orderCounts.supplier_confirmation_needs_verification === 1 ? '' : 's'}`,
                        subtitle: 'Inbound confirmation received — review and approve.',
                        subtitleAsTooltip: true,
                        onClick: () => { if (singleDeepLink(actionsSummary?.supplier_confirmation_needs_verification_order_ids, '#supplier')) return; setAttentionFilters(['supplier_confirmation_needs_verification']); setStatusFilter('all'); },
                        resolveScope: 'supplier-confirmation',
                    }));
                    if (orderCounts.tp_confirmation_needs_verification > 0) internal.push(renderCard({
                        key: 'tpcv', testId: 'attention-tp-confirmation-needs-verification', dot: 'amber',
                        count: orderCounts.tp_confirmation_needs_verification, titleFirst: 'Verify',
                        title: `Test Press confirmation${orderCounts.tp_confirmation_needs_verification === 1 ? '' : 's'}`,
                        subtitle: 'Inbound confirmation received — review and approve.',
                        subtitleAsTooltip: true,
                        onClick: () => { if (singleDeepLink(actionsSummary?.tp_confirmation_needs_verification_order_ids, '#test-press')) return; setAttentionFilters(['tp_confirmation_needs_verification']); setStatusFilter('all'); },
                        resolveScope: 'tp-confirmation',
                    }));
                    // Internal: customer purchased Test Pressings but admin
                    // hasn't sent the Sony PO yet (test_press_status =
                    // "required"). Auto-clears once the state machine moves
                    // to "ordered" from Supplier → Test Press → PO send.
                    if (orderCounts.tp_order_to_sony_pending > 0) internal.push(renderCard({
                        key: 'tp-order-pending', testId: 'attention-tp-order-to-sony-pending', dot: 'red',
                        count: orderCounts.tp_order_to_sony_pending, titleFirst: 'Order',
                        title: `Test Press${orderCounts.tp_order_to_sony_pending === 1 ? '' : 'es'}`,
                        subtitle: 'Customer purchased Test Pressings — send the Sony PO.',
                        subtitleAsTooltip: true,
                        onClick: () => { if (singleDeepLink(actionsSummary?.tp_order_to_sony_pending_order_ids, '#test-press')) return; setAttentionFilters(['tp_order_to_sony_pending']); setStatusFilter('all'); },
                    }));
                    // Internal: customer rejected the TP and admin still
                    // needs to forward the feedback to Sony. Auto-clears
                    // the moment the TP state machine advances to
                    // "awaiting_feedback" (Phase 3 "Send feedback" dialog).
                    if (orderCounts.tp_send_feedback_to_sony > 0) internal.push(renderCard({
                        key: 'tp-send-feedback', testId: 'attention-tp-send-feedback-to-sony', dot: 'red',
                        count: orderCounts.tp_send_feedback_to_sony, titleFirst: 'Send',
                        title: `Test Press feedback to supplier`,
                        subtitle: 'Customer rejected the Test Press — forward feedback to the pressing plant.',
                        subtitleAsTooltip: true,
                        onClick: () => { if (singleDeepLink(actionsSummary?.tp_send_feedback_to_sony_order_ids, '#test-press')) return; setAttentionFilters(['tp_send_feedback_to_sony']); setStatusFilter('all'); },
                    }));
                    if (orderCounts.dispatch_needs_verification > 0) internal.push(renderCard({
                        key: 'dnv', testId: 'attention-dispatch-needs-verification', dot: 'amber',
                        count: orderCounts.dispatch_needs_verification, titleFirst: 'Review',
                        title: `dispatch import${orderCounts.dispatch_needs_verification === 1 ? '' : 's'}`,
                        subtitle: 'Inbound Hub imported dispatch details - approve before sending notification.',
                        subtitleAsTooltip: true,
                        onClick: () => { if (singleDeepLink(actionsSummary?.dispatch_needs_verification_order_ids, '#supplier')) return; setAttentionFilters(['dispatch_needs_verification']); setStatusFilter('all'); },
                        resolveScope: 'dispatch-information',
                    }));
                    // Separate TP-dispatch bucket. Kept disjoint from the
                    // production one above so admins can tell at a glance
                    // which workflow needs review and the counts never
                    // entangle. Reuses the same approve/clear plumbing on
                    // the backend (same SC collection, variant filter).
                    if (orderCounts.tp_dispatch_needs_verification > 0) internal.push(renderCard({
                        key: 'tp_dnv', testId: 'attention-tp-dispatch-needs-verification', dot: 'amber',
                        count: orderCounts.tp_dispatch_needs_verification, titleFirst: 'Review',
                        title: `Test Press dispatch${orderCounts.tp_dispatch_needs_verification === 1 ? '' : 's'}`,
                        subtitle: 'Inbound Hub imported Test Press dispatch details - approve inside Supplier → Test Press.',
                        subtitleAsTooltip: true,
                        onClick: () => { if (singleDeepLink(actionsSummary?.tp_dispatch_needs_verification_order_ids, '#test-press')) return; setAttentionFilters(['tp_dispatch_needs_verification']); setStatusFilter('all'); },
                    }));
                    if (orderCounts.supplier_errors > 0) internal.push(renderCard({
                        key: 'serr', testId: 'attention-supplier-errors', dot: 'red',
                        count: orderCounts.supplier_errors, titleFirst: 'Resolve',
                        title: `supplier error${orderCounts.supplier_errors === 1 ? '' : 's'}`,
                        subtitle: 'Sony returned an error on master or artwork.',
                        subtitleAsTooltip: true,
                        onClick: () => { setAttentionFilters(['supplier_error']); setStatusFilter('all'); },
                    }));
                    // ─── Supplier RFQ lifecycle ──────────────────
                    // Three disjoint-at-the-UI counters derived from
                    // orders.rfq_sends[]. "Awaiting" is informational
                    // (shown here but excluded from the top badge
                    // aggregate — see backend ``total`` composition).
                    if (orderCounts.rfq_to_send > 0) internal.push(renderCard({
                        key: 'rfq-to-send', testId: 'attention-rfq-to-send', dot: 'orange',
                        count: orderCounts.rfq_to_send, titleFirst: 'Send',
                        title: `supplier RFQ${orderCounts.rfq_to_send === 1 ? '' : 's'}`,
                        subtitle: 'BOM has lines without a supplier price — send a Request For Quotation.',
                        subtitleAsTooltip: true,
                        onClick: () => {
                            const ids = actionsSummary?.rfq_to_send_order_ids || [];
                            if (ids.length === 1) {
                                navigate(`/orders/${ids[0]}#finance`);
                                return;
                            }
                            setAttentionFilters(['rfq_to_send']);
                            setStatusFilter('all');
                        },
                    }));
                    if (orderCounts.rfq_overdue > 0) internal.push(renderCard({
                        key: 'rfq-overdue', testId: 'attention-rfq-overdue', dot: 'red',
                        count: orderCounts.rfq_overdue, titleFirst: 'Chase',
                        title: `overdue supplier quote${orderCounts.rfq_overdue === 1 ? '' : 's'}`,
                        subtitle: `Supplier has not replied within ${actionsSummary?.rfq_overdue_threshold_days || 5} business days.`,
                        subtitleAsTooltip: true,
                        onClick: () => {
                            const ids = actionsSummary?.rfq_overdue_order_ids || [];
                            if (ids.length === 1) {
                                navigate(`/orders/${ids[0]}#finance`);
                                return;
                            }
                            setAttentionFilters(['rfq_overdue']);
                            setStatusFilter('all');
                        },
                    }));
                    if (orderCounts.ready_for_production > 0) internal.push(renderCard({
                        key: 'rfp', testId: 'attention-ready-for-production', dot: 'orange',
                        count: orderCounts.ready_for_production, titleFirst: 'Create production order for',
                        title: `order${orderCounts.ready_for_production === 1 ? '' : 's'}`,
                        subtitle: 'All upstream steps done - waiting for Sony PO / BOM.',
                        subtitleAsTooltip: true,
                        onClick: () => { if (singleDeepLink(actionsSummary?.ready_for_production_order_ids, '#supplier')) return; setAttentionFilters(['ready_for_production']); setStatusFilter('all'); },
                    }));
                    // Orders awaiting Sony placement — broader than
                    // ``ready_for_production``: catches ANY active
                    // production order (incl. reorders) where the
                    // Sony PO has not yet been sent, regardless of
                    // artwork/master readiness. Auto-clears via
                    // ``po_sent_at`` on the production supplier_confirmation.
                    // Single-order click deep-links straight to the
                    // order detail's Supplier → Production Order
                    // section via the ``#supplier`` hash.
                    if (orderCounts.orders_awaiting_sony_placement > 0) internal.push(renderCard({
                        key: 'oasp', testId: 'attention-orders-awaiting-sony-placement', dot: 'orange',
                        count: orderCounts.orders_awaiting_sony_placement, titleFirst: '',
                        title: `order${orderCounts.orders_awaiting_sony_placement === 1 ? '' : 's'} awaiting Sony placement`,
                        subtitle: 'Production Order has not yet been sent to Sony (reorders included).',
                        subtitleAsTooltip: true,
                        onClick: () => {
                            const ids = actionsSummary?.orders_awaiting_sony_placement_order_ids || [];
                            if (ids.length === 1) {
                                navigate(`/orders/${ids[0]}#supplier`);
                                return;
                            }
                            setAttentionFilters(['orders_awaiting_sony_placement']);
                            setStatusFilter('all');
                        },
                    }));
                    if (orderCounts.supplier_validation_mismatch > 0) internal.push(renderCard({
                        key: 'svm', testId: 'attention-supplier-validation-mismatch', dot: 'red',
                        count: orderCounts.supplier_validation_mismatch, titleFirst: 'Resolve',
                        title: `supplier validation mismatch${orderCounts.supplier_validation_mismatch === 1 ? '' : 'es'}`,
                        subtitle: 'Sony vs FLOW mismatch - missing files, wrong count, or checksum mismatch.',
                        subtitleAsTooltip: true,
                        onClick: () => { if (singleDeepLink(actionsSummary?.supplier_validation_mismatch_order_ids, '#supplier')) return; setAttentionFilters(['supplier_validation_mismatch']); setStatusFilter('all'); },
                    }));
                    if (orderCounts.master_ready_not_sent > 0) internal.push(renderCard({
                        key: 'mrns', testId: 'attention-master-ready-not-sent', dot: 'indigo',
                        count: orderCounts.master_ready_not_sent, titleFirst: 'Send',
                        title: `master${orderCounts.master_ready_not_sent === 1 ? '' : 's'} to Sony`,
                        subtitle: 'Master is valid, but not yet flagged as sent to Sony.',
                        subtitleAsTooltip: true,
                        onClick: () => { if (singleDeepLink(actionsSummary?.master_ready_not_sent_order_ids, '#audio')) return; setAttentionFilters(['master_ready_not_sent']); setStatusFilter('all'); },
                    }));
                    if (orderCounts.master_verification_pending > 0) internal.push(renderCard({
                        key: 'mvp', testId: 'attention-master-verification-pending', dot: 'rose',
                        count: orderCounts.master_verification_pending, titleFirst: 'Verify',
                        title: `master${orderCounts.master_verification_pending === 1 ? '' : 's'} pending verification`,
                        subtitle: 'Master uploaded — MD5 or manifest verification still pending or needs action.',
                        subtitleAsTooltip: true,
                        onClick: () => { if (singleDeepLink(actionsSummary?.master_verification_pending_order_ids, '#audio')) return; setAttentionFilters(['master_verification_pending']); setStatusFilter('all'); },
                    }));
                    if (orderCounts.sony_upload_failed_or_stale > 0) internal.push(renderCard({
                        key: 'sony-upload-fail', testId: 'attention-sony-upload-failed-or-stale', dot: 'red',
                        count: orderCounts.sony_upload_failed_or_stale, titleFirst: 'Fix',
                        title: `Sony upload job${orderCounts.sony_upload_failed_or_stale === 1 ? '' : 's'} failed or stalled`,
                        subtitle: 'Worker crashed or reported failure - retry required.',
                        subtitleAsTooltip: true,
                        onClick: () => navigate('/admin/sony-upload-jobs?status=failed'),
                    }));
                    if (orderCounts.inbound_documents_needs_review > 0) internal.push(renderCard({
                        key: 'idnr', testId: 'attention-inbound-documents-needs-review', dot: 'amber',
                        count: orderCounts.inbound_documents_needs_review, titleFirst: 'Review',
                        title: `inbound document${orderCounts.inbound_documents_needs_review === 1 ? '' : 's'}`,
                        subtitle: 'Inbound webhook could not auto-link - manual review required.',
                        subtitleAsTooltip: true,
                        onClick: () => navigate('/admin/inbound-documents?status=needs_review'),
                    }));

                    const waiting = [];
                    if (orderCounts.missing_artwork > 0) waiting.push(renderCard({
                        key: 'art', testId: 'attention-missing-artwork', dot: 'purple',
                        count: orderCounts.missing_artwork,
                        title: `order${orderCounts.missing_artwork === 1 ? '' : 's'} missing artwork`,
                        onClick: () => { if (singleDeepLink(actionsSummary?.missing_artwork_order_ids, '#artwork')) return; setAttentionFilters(['artwork']); setStatusFilter('all'); },
                    }));
                    if (orderCounts.missing_master > 0) waiting.push(renderCard({
                        key: 'mmaster', testId: 'attention-missing-master', dot: 'purple',
                        count: orderCounts.missing_master,
                        title: `order${orderCounts.missing_master === 1 ? '' : 's'} missing master`,
                        subtitle: 'No master uploaded yet (or a rejected/incomplete upload awaiting the customer).',
                        subtitleAsTooltip: true,
                        onClick: () => { if (singleDeepLink(actionsSummary?.missing_master_order_ids, '#audio')) return; setAttentionFilters(['missing_master']); setStatusFilter('all'); },
                    }));                    if (orderCounts.awaiting_approval > 0) waiting.push(renderCard({
                        key: 'approve', testId: 'attention-awaiting-approval', dot: 'purple',
                        count: orderCounts.awaiting_approval,
                        title: `artwork approval${orderCounts.awaiting_approval === 1 ? '' : 's'} waiting for customer`,
                        onClick: () => navigate('/artwork-jobs?status=Awaiting_Approval'),
                    }));
                    if (orderCounts.ncb_waiting_for_customer > 0) waiting.push(renderCard({
                        key: 'ncb', testId: 'attention-ncb-waiting-for-customer', dot: 'purple',
                        count: orderCounts.ncb_waiting_for_customer, titleFirst: '',
                        title: `order${orderCounts.ncb_waiting_for_customer === 1 ? '' : 's'} waiting for NCB clearance`,
                        subtitle: 'NCB clearance is still pending with the customer.',
                        subtitleAsTooltip: true,
                        onClick: () => { if (singleDeepLink(actionsSummary?.ncb_waiting_for_customer_order_ids, '#ncb')) return; setAttentionFilters(['ncb']); setStatusFilter('all'); },
                        sideMenu: (
                            <NCBReminderMenu
                                orderIds={actionsSummary?.ncb_waiting_for_customer_order_ids || []}
                                orders={loadedOrders || []}
                                onSent={fetchActionsSummary}
                            />
                        ),
                    }));
                    if (orderCounts.test_press_awaiting_customer > 0) waiting.push(renderCard({
                        key: 'tp-review', testId: 'attention-test-press-awaiting-customer', dot: 'amber',
                        count: orderCounts.test_press_awaiting_customer, titleFirst: '',
                        title: `Test Press${orderCounts.test_press_awaiting_customer === 1 ? '' : 'es'} awaiting customer approval`,
                        subtitle: 'Delivered to customer — approve or reject still pending.',
                        subtitleAsTooltip: true,
                        onClick: () => { if (singleDeepLink(actionsSummary?.test_press_awaiting_customer_order_ids, '#test-press')) return; setAttentionFilters(['test_press_awaiting_customer']); setStatusFilter('all'); },
                    }));

                    const waitingSupplier = [];
                    if (orderCounts.waiting_for_supplier > 0) waitingSupplier.push(renderCard({
                        key: 'wfs', testId: 'attention-waiting-for-supplier', dot: 'sky',
                        count: orderCounts.waiting_for_supplier,
                        title: `PO${orderCounts.waiting_for_supplier === 1 ? '' : 's'} awaiting supplier confirmation`,
                        subtitle: 'Production Order sent to Sony - no Order Confirmation received yet.',
                        subtitleAsTooltip: true,
                        onClick: () => { if (singleDeepLink(actionsSummary?.waiting_for_supplier_order_ids, '#supplier')) return; setAttentionFilters(['waiting_for_supplier']); setStatusFilter('all'); },
                    }));
                    // Waiting for Supplier — Phase 3 feedback has been
                    // emailed to Sony and we're waiting for Sony's
                    // reply. Auto-clears when the admin applies a
                    // Phase 4 response branch.
                    if (orderCounts.tp_awaiting_sony_response > 0) waitingSupplier.push(renderCard({
                        key: 'tp-awaiting-sony', testId: 'attention-tp-awaiting-sony-response', dot: 'sky',
                        count: orderCounts.tp_awaiting_sony_response,
                        title: `Test Press${orderCounts.tp_awaiting_sony_response === 1 ? '' : 'es'} awaiting Sony response`,
                        subtitle: 'Feedback sent to Sony — waiting for their response (V2 / fix / new master).',
                        subtitleAsTooltip: true,
                        onClick: () => { if (singleDeepLink(actionsSummary?.tp_awaiting_sony_response_order_ids, '#test-press')) return; setAttentionFilters(['tp_awaiting_sony_response']); setStatusFilter('all'); },
                    }));
                    // Supplier RFQ sent, waiting for the supplier's reply.
                    // Moved here (previously lived in Internal Action) so
                    // the categorisation matches the next-action owner:
                    // once the RFQ is out the door, the admin is waiting
                    // on the supplier, not performing an internal action.
                    // Overdue stays in Internal Action because *chasing*
                    // IS the admin's next step.
                    if (orderCounts.rfq_awaiting > 0) waitingSupplier.push(renderCard({
                        key: 'rfq-awaiting', testId: 'attention-rfq-awaiting', dot: 'sky',
                        count: orderCounts.rfq_awaiting, titleFirst: 'Awaiting',
                        title: `supplier quote${orderCounts.rfq_awaiting === 1 ? '' : 's'}`,
                        subtitle: 'RFQ sent — waiting for the supplier to reply.',
                        subtitleAsTooltip: true,
                        onClick: () => {
                            const ids = actionsSummary?.rfq_awaiting_order_ids || [];
                            if (ids.length === 1) {
                                navigate(`/orders/${ids[0]}#finance`);
                                return;
                            }
                            setAttentionFilters(['rfq_awaiting']);
                            setStatusFilter('all');
                        },
                    }));

                    const followup = [];
                    if (finance.ready_to_invoice > 0) followup.push(renderCard({
                        key: 'rti', testId: 'attention-ready-to-invoice', dot: 'emerald',
                        count: finance.ready_to_invoice,
                        title: `order${finance.ready_to_invoice === 1 ? '' : 's'} ready to invoice`,
                        onClick: () => { setAttentionFilters(['ready_to_invoice']); setStatusFilter('all'); },
                    }));
                    if (orderCounts.low_margin > 0) followup.push(renderCard({
                        key: 'lm', testId: 'attention-low-margin', dot: 'orange',
                        count: orderCounts.low_margin,
                        title: `order${orderCounts.low_margin === 1 ? '' : 's'} with low margin (<20%)`,
                        onClick: () => { setAttentionFilters(['low_margin']); setStatusFilter('all'); },
                    }));
                    if (finance.companies_with_overdue > 0) followup.push(renderCard({
                        key: 'cwo', testId: 'attention-companies-with-overdue', dot: 'orange',
                        count: finance.companies_with_overdue,
                        title: `customer${finance.companies_with_overdue === 1 ? '' : 's'} with overdue balance`,
                        // Admin action — go to the admin finance overview
                        // filtered to overdue companies, NOT the current user's
                        // own /invoices page.
                        onClick: () => navigate('/finance/invoices?filter=overdue'),
                    }));

                    const adminItems = [];
                    if (users.pending_approvals > 0) adminItems.push(renderCard({
                        key: 'pa', testId: 'attention-pending-approvals', dot: 'amber',
                        count: users.pending_approvals,
                        title: `organization${users.pending_approvals === 1 ? '' : 's'} awaiting approval`,
                        onClick: () => navigate('/users?filter=pending'),
                    }));

                    // Only reserve grid slots for columns that actually have
                    // cards, so 3 populated columns each get 1/3 of the width
                    // rather than being crushed into a fixed 5-col grid.
                    const columns = [
                        ['Internal Action - today', 'red', internal, 'attention-col-internal'],
                        ['Waiting for Customer', 'amber', waiting, 'attention-col-waiting'],
                        ['Waiting for Supplier', 'sky', waitingSupplier, 'attention-col-waiting-supplier'],
                        ['Follow-up', 'blue', followup, 'attention-col-followup'],
                        ['Admin / Account Tasks', 'slate', adminItems, 'attention-col-admin'],
                    ].filter(([, , items]) => items.length > 0);
                    // Tailwind needs static class names to be picked up by the
                    // JIT compiler, so we map the active-count to a fixed set
                    // of grid utility classes rather than composing them.
                    const gridColsClass = {
                        1: 'grid-cols-1',
                        2: 'grid-cols-1 md:grid-cols-2',
                        3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
                        4: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4',
                        5: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5',
                    }[columns.length] || 'grid-cols-1';

                    return (
                        <Card className="mb-6 border-amber-200 bg-amber-50/40">
                            <CardHeader className="pb-3">
                                <CardTitle className="text-base flex items-center gap-2">
                                    <AlertTriangle className="h-4 w-4 text-amber-600" />
                                    Needs Attention
                                    <Badge variant="outline" className="ml-2 bg-amber-100 text-amber-700 border-amber-300">
                                        {actionsSummary.total_actions}
                                    </Badge>
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="pt-0">
                                <div className={`grid ${gridColsClass} gap-4`} data-testid="attention-columns-grid" data-active-columns={columns.length}>
                                    {columns.map(([title, colour, items, testId]) => renderColumn(title, colour, items, testId))}
                                </div>
                            </CardContent>
                        </Card>
                    );
                })()}

                {/* Compact business snapshot — customer only. Admin KPIs
                    moved to the dedicated /admin/overview page so the Orders
                    page stays focused on operations. Status filter pills +
                    search have been moved down to sit directly above the
                    order table (see below). */}

                {/* Credit Limit Warning (Customer) */}
                {!isAdmin && creditStatus?.is_blocked && (
                    <Card className="border-red-300 bg-red-50 mb-4" data-testid="credit-blocked-banner">
                        <CardContent className="flex items-center gap-3 py-4">
                            <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0" />
                            <div className="flex-1">
                                <p className="font-medium text-red-800">{t('dashboard.submission_blocked_title', 'Order submission is temporarily blocked')}</p>
                                <p className="text-sm text-red-600">
                                    {creditStatus.block_reason}. 
                                    <button onClick={() => navigate('/invoices')} className="ml-1 underline font-medium hover:text-red-800">{t('dashboard.view_invoices', 'View invoices')}</button>
                                </p>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Customer dashboard — action center + activity feed sit
                    above the order list so a returning customer can
                    answer "what do I need to do?" and "what changed?"
                    without scanning the whole table. */}
                {!isAdmin && orders.length > 0 && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <CustomerActionCenter
                            orders={orders}
                            unpaidInvoices={[]}
                        />
                        <CustomerActivityFeed
                            orders={orders}
                            previousLastLogin={user?.previous_last_login}
                        />
                    </div>
                )}

                {/* Standalone artwork-revision + artwork-approval banners
                    were removed — Your Action Center above already surfaces
                    the same info as a clickable to-do item, so this is the
                    single place for customer actions now. */}

                {/* Search and Filters — customer status pills + search
                    moved INSIDE the Orders card below (they belong to the
                    table, not the dashboard). Admin still has its own
                    outside filter bar for the wider filter set. */}
                <div className="mb-6 space-y-3">
                    {/* Admin Filter Bar */}
                    {isAdmin && (
                        <div className="bg-white rounded-lg border border-border shadow-premium-sm p-3">
                            <div className="flex flex-wrap items-center gap-4">
                                {/* Search (treated as another filter, kept
                                    inline with the rest of the filter row so
                                    the order list can float higher up). */}
                                <div className="relative w-[220px]">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                    <Input
                                        placeholder={t('dashboard.search', 'Search orders...')}
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="pl-9 h-8 text-sm"
                                        data-testid="search-orders-input"
                                    />
                                </div>

                                {/* Divider */}
                                <div className="h-6 w-px bg-slate-200" />

                                {/* Customer Filter */}
                                <div className="flex items-center gap-2">
                                    <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">Customer</span>
                                    <Popover open={customerSearchOpen} onOpenChange={setCustomerSearchOpen}>
                                        <PopoverTrigger asChild>
                                            <Button
                                                variant="outline"
                                                role="combobox"
                                                aria-expanded={customerSearchOpen}
                                                className="w-[200px] justify-between h-8 text-sm"
                                                data-testid="customer-filter"
                                            >
                                                {customerFilter === 'all' 
                                                    ? t('filters.all_customers', 'All customers')
                                                    : (customers.find(c => c.id === customerFilter)?.name || 'Select...')}
                                                <ChevronsUpDown className="ml-2 h-3 w-3 shrink-0 opacity-50" />
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-[250px] p-0" align="start">
                                            <Command>
                                                <CommandInput placeholder={t('filters.search_customer', 'Search customer...')} className="h-9" />
                                                <CommandList>
                                                    <CommandEmpty>{t('filters.no_customer_found', 'No customer found')}</CommandEmpty>
                                                    <CommandGroup>
                                                        <CommandItem
                                                            value="all"
                                                            onSelect={() => {
                                                                setCustomerFilter('all');
                                                                setCustomerSearchOpen(false);
                                                            }}
                                                        >
                                                            <Check className={`mr-2 h-4 w-4 ${customerFilter === 'all' ? 'opacity-100' : 'opacity-0'}`} />
                                                            {t('filters.all_customers', 'All customers')}
                                                        </CommandItem>
                                                        {customers.map((customer) => (
                                                            <CommandItem
                                                                key={customer.id}
                                                                value={customer.name}
                                                                onSelect={() => {
                                                                    setCustomerFilter(customer.id);
                                                                    setCustomerSearchOpen(false);
                                                                }}
                                                                data-testid={`customer-filter-option-${customer.id}`}
                                                            >
                                                                <Check className={`mr-2 h-4 w-4 ${customerFilter === customer.id ? 'opacity-100' : 'opacity-0'}`} />
                                                                <span>{customer.name}</span>
                                                            </CommandItem>
                                                        ))}
                                                    </CommandGroup>
                                                </CommandList>
                                            </Command>
                                        </PopoverContent>
                                    </Popover>
                                </div>

                                {/* Divider */}
                                <div className="h-6 w-px bg-slate-200" />

                                {/* Status Filter - Pill Buttons.
                                    Represents the actual order lifecycle
                                    consistently. Each pill maps to one
                                    (or a well-defined bundle of) DB status
                                    value(s). `all` hides completed +
                                    cancelled by default; explicit
                                    "Completed" pill opts back in. */}
                                <div className="flex items-center gap-2">
                                    <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">Status</span>
                                    <div className="flex gap-1 flex-wrap">
                                        {[
                                            { value: 'all', label: t('filters.all', 'All') },
                                            { value: 'quote', label: t('filters.quote', 'Quote') },
                                            { value: 'draft', label: t('filters.draft', 'Draft') },
                                            { value: 'submitted', label: t('filters.submitted', 'Submitted') },
                                            { value: 'in_progress', label: t('filters.in_progress', 'In Progress') },
                                            { value: 'on_hold', label: t('filters.on_hold', 'On Hold') },
                                            { value: 'shipping', label: t('filters.shipping', 'Shipping') },
                                            { value: 'completed', label: t('filters.completed', 'Completed') },
                                            // Admin-only "Draft Archive" pill — surfaces Drafts hidden from
                                            // the default view because they've had no activity for 14+ days.
                                            ...(isAdmin
                                                ? [{ value: 'draft_archive', label: t('filters.draft_archive', 'Draft Archive'), adminOnly: true }]
                                                : []),
                                        ].map((status) => {
                                            const isArchive = status.value === 'draft_archive';
                                            const count = isArchive
                                                ? orders.filter(isDraftArchived).length
                                                : null;
                                            return (
                                                <button
                                                    key={status.value}
                                                    onClick={() => setStatusFilter(status.value)}
                                                    title={isArchive
                                                        ? 'Draft orders with no customer/order activity for 14+ days. Hidden from the default view — status is still Draft.'
                                                        : undefined}
                                                    className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                                                        statusFilter === status.value
                                                            ? 'bg-slate-900 text-white'
                                                            : isArchive
                                                                ? 'bg-slate-50 text-slate-500 hover:bg-slate-100 border border-dashed border-slate-300'
                                                                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                                    }`}
                                                    data-testid={`status-filter-${status.value}`}
                                                >
                                                    {status.label}
                                                    {isArchive && count > 0 && (
                                                        <span className={`ml-1 ${statusFilter === status.value ? 'text-white/70' : 'text-slate-400'}`}>({count})</span>
                                                    )}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* Divider */}
                                <div className="h-6 w-px bg-slate-200" />
                                
                                {/* Product Type Filter - Pill Buttons */}
                                <div className="flex items-center gap-2">
                                    <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">Type</span>
                                    <div className="flex gap-1">
                                        {[
                                            { value: 'all', label: 'All', icon: null },
                                            { value: 'vinyl', label: 'Vinyl', icon: '🎵' },
                                            { value: 'cd', label: 'CD', icon: 'cd' },
                                            { value: 'dvd', label: 'DVD', icon: 'dvd' },
                                            { value: 'bluray', label: 'Blu-ray', icon: 'bluray' },
                                        ].map((type) => (
                                            <button
                                                key={type.value}
                                                onClick={() => setProductTypeFilter(type.value)}
                                                className={`px-3 py-1 text-xs font-medium rounded-full transition-colors flex items-center gap-1 ${
                                                    productTypeFilter === type.value
                                                        ? 'bg-slate-900 text-white'
                                                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                                }`}
                                                data-testid={`type-filter-${type.value}`}
                                            >
                                                {type.icon && (
                                                    type.icon === '🎵' 
                                                        ? <span>{type.icon}</span> 
                                                        : <MediaDiscIcon type={type.icon} size={14} />
                                                )}
                                                {type.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Order Type Filter (New / Reorder) */}
                                <div className="flex items-center gap-2">
                                    <div className="flex items-center gap-1 bg-slate-50 rounded-lg p-0.5">
                                        {[
                                            { value: 'all', label: 'All' },
                                            { value: 'new', label: 'New' },
                                            { value: 'reorder', label: 'Re-order' },
                                        ].map((ot) => (
                                            <button
                                                key={ot.value}
                                                onClick={() => setOrderTypeFilter(ot.value)}
                                                className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                                                    orderTypeFilter === ot.value
                                                        ? ot.value === 'new' ? 'bg-emerald-600 text-white' 
                                                          : ot.value === 'reorder' ? 'bg-blue-600 text-white'
                                                          : 'bg-slate-900 text-white'
                                                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                                }`}
                                                data-testid={`order-type-filter-${ot.value}`}
                                            >
                                                {ot.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Divider */}
                                <div className="h-6 w-px bg-slate-200" />

                                {/* Attention Filters - Checkboxes */}
                                <div className="flex items-center gap-3">
                                    <span className="text-xs font-medium text-slate-500 uppercase tracking-wider flex items-center gap-1">
                                        <AlertTriangle className="h-3 w-3" />
                                        Attention
                                    </span>
                                    <div className="flex flex-wrap gap-3">
                                        <label className="flex items-center gap-1.5 cursor-pointer group">
                                            <Checkbox
                                                checked={attentionFilters.includes('artwork')}
                                                onCheckedChange={() => toggleAttentionFilter('artwork')}
                                                data-testid="attention-artwork"
                                            />
                                            <span className={`text-xs ${attentionFilters.includes('artwork') ? 'text-slate-900 font-medium' : 'text-slate-600'} group-hover:text-slate-900`}>
                                                <Palette className="h-3 w-3 inline mr-0.5" />
                                                {t('filters.artwork_pending', 'Artwork')}
                                            </span>
                                        </label>
                                        <label className="flex items-center gap-1.5 cursor-pointer group">
                                            <Checkbox
                                                checked={attentionFilters.includes('audio')}
                                                onCheckedChange={() => toggleAttentionFilter('audio')}
                                                data-testid="attention-audio"
                                            />
                                            <span className={`text-xs ${attentionFilters.includes('audio') ? 'text-slate-900 font-medium' : 'text-slate-600'} group-hover:text-slate-900`}>
                                                {t('filters.audio_missing', 'Master')}
                                            </span>
                                        </label>
                                        <label className="flex items-center gap-1.5 cursor-pointer group">
                                            <Checkbox
                                                checked={attentionFilters.includes('ncb')}
                                                onCheckedChange={() => toggleAttentionFilter('ncb')}
                                                data-testid="attention-ncb"
                                            />
                                            <span className={`text-xs ${attentionFilters.includes('ncb') ? 'text-slate-900 font-medium' : 'text-slate-600'} group-hover:text-slate-900`}>
                                                <Shield className="h-3 w-3 inline mr-0.5" />
                                                {t('filters.ncb_pending', 'NCB')}
                                            </span>
                                        </label>
                                        <label className="flex items-center gap-1.5 cursor-pointer group">
                                            <Checkbox
                                                checked={attentionFilters.includes('supplier')}
                                                onCheckedChange={() => toggleAttentionFilter('supplier')}
                                                data-testid="attention-supplier"
                                            />
                                            <span className={`text-xs ${attentionFilters.includes('supplier') ? 'text-slate-900 font-medium' : 'text-slate-600'} group-hover:text-slate-900`}>
                                                <Truck className="h-3 w-3 inline mr-0.5" />
                                                {t('filters.supplier_pending', 'Supplier')}
                                            </span>
                                        </label>
                                    </div>
                                </div>

                                {/* Clear Filters */}
                                {hasActiveFilters && (
                                    <>
                                        <div className="h-6 w-px bg-slate-200" />
                                        <button
                                            onClick={clearFilters}
                                            className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1"
                                            data-testid="clear-filters"
                                        >
                                            <X className="h-3 w-3" />
                                            {t('filters.clear', 'Clear filters')}
                                        </button>
                                    </>
                                )}
                            </div>
                            
                            {/* Results count */}
                            {hasActiveFilters && (
                                <div className="mt-2 pt-2 border-t border-slate-100 text-xs text-slate-500">
                                    {t('filters.showing', 'Showing')} <span className="font-medium text-slate-700">{filteredOrders.length}</span> {t('filters.of', 'of')} {orders.length} {t('filters.orders', 'orders')}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Orders Table — for new customers (0 orders) we replace
                    the whole card with a standalone welcome state so there
                    are no order-management widgets at all. */}
                {!isAdmin && orders.length === 0 ? (
                    <div
                        className="rounded-lg border border-slate-200 bg-white p-12 text-center"
                        data-testid="customer-empty-state"
                    >
                        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-slate-50 mb-4">
                            <Package className="h-7 w-7 text-slate-400" />
                        </div>
                        <h2
                            className="text-xl font-semibold text-slate-900 mb-1"
                            style={{ fontFamily: 'Inter, sans-serif' }}
                        >
                            {t('dashboard.welcome', 'Welcome! No orders yet.')}
                        </h2>
                        <p className="text-sm text-slate-500 mb-6 max-w-md mx-auto">
                            {t('dashboard.getstarted', 'Create your first order to get started.')}
                        </p>
                        <Button
                            onClick={handleQuickNewOrder}
                            disabled={isCreating}
                            className="bg-slate-900 hover:bg-slate-800"
                            data-testid="empty-state-create-order-btn"
                        >
                            <Plus className="h-4 w-4 mr-2" />
                            {isCreating ? 'Creating...' : t('order.create.button', 'Create Order')}
                        </Button>
                    </div>
                ) : (
                <Card className="border border-border shadow-premium-sm">
                    <CardHeader className="border-b border-slate-100 py-3 px-4">
                        <CardTitle className="text-base font-semibold" style={{ fontFamily: 'Inter, sans-serif' }}>
                            {t('table.orders', 'Orders')}
                        </CardTitle>
                    </CardHeader>
                    {/* Customer status pills + search live INSIDE the card so
                        it's obvious they filter the table below them. Only
                        rendered for customers with at least one order. */}
                    {!isAdmin && orders.length > 0 && (
                        <div
                            className="px-4 pt-3 pb-3 border-b border-slate-100 space-y-3"
                            data-testid="customer-status-filter-bar"
                        >
                            {/* Quick-filter chip — set by Action Center clicks.
                                Overrides the pill row so the customer sees
                                exactly the slice they asked for; click × to
                                clear and drop back to normal browsing. */}
                            {quickFilter && (
                                <div
                                    className="flex items-center gap-2 bg-indigo-50 border border-indigo-200 rounded-md px-3 py-2 text-sm"
                                    data-testid="customer-quick-filter-chip"
                                >
                                    <span className="text-indigo-700 font-medium">
                                        Showing: {{
                                            'artwork-approval': 'Artwork ready for your approval',
                                            'artwork-revision': 'Artwork needing revision',
                                            'artwork-required': 'Orders needing artwork',
                                            'missing-master':   'Orders missing master files',
                                            'quote':            'Quotes waiting for approval',
                                            'test-press-review': 'Test presses ready for review',
                                        }[quickFilter] || quickFilter}
                                    </span>
                                    <span className="text-indigo-500 text-xs">
                                        · {filteredOrders.length} order{filteredOrders.length === 1 ? '' : 's'}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setQuickFilter(null);
                                            const next = new URLSearchParams(searchParams);
                                            next.delete('filter');
                                            setSearchParams(next, { replace: true });
                                        }}
                                        className="ml-auto text-indigo-500 hover:text-indigo-700 font-semibold"
                                        aria-label="Clear filter"
                                        data-testid="customer-quick-filter-clear"
                                    >
                                        ×
                                    </button>
                                </div>
                            )}
                            <div className="flex flex-wrap items-center gap-1 border-b border-slate-200">
                                {[
                                    { key: 'all',         label: t('filters.all', 'All'),         count: totalOrders,      icon: Package,     activeText: 'text-slate-900',   badge: 'bg-slate-200 text-slate-700' },
                                    { key: 'in_progress', label: t('filters.in_progress', 'In Progress'), count: inProgressOrders, icon: Clock,       activeText: 'text-amber-700',   badge: 'bg-amber-100 text-amber-700' },
                                    { key: 'completed',   label: t('filters.completed', 'Completed'),   count: completedOrders,  icon: CheckCircle, activeText: 'text-emerald-700', badge: 'bg-emerald-100 text-emerald-700' },
                                    { key: 'draft',       label: t('filters.drafts', 'Drafts'),      count: draftOrders,      icon: AlertCircle, activeText: 'text-slate-700',   badge: 'bg-slate-200 text-slate-700' },
                                ].map((tab) => {
                                    const active = activeTab === tab.key;
                                    const Icon = tab.icon;
                                    return (
                                        <button
                                            key={tab.key}
                                            type="button"
                                            onClick={() => {
                                                setActiveTab(tab.key);
                                                // Any pill click means the user wants the
                                                // regular status view — cancel any active
                                                // quick-filter and prune it from the URL.
                                                if (quickFilter) {
                                                    setQuickFilter(null);
                                                    const next = new URLSearchParams(searchParams);
                                                    next.delete('filter');
                                                    setSearchParams(next, { replace: true });
                                                }
                                            }}
                                            className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                                                active
                                                    ? `${tab.activeText} border-current`
                                                    : 'text-slate-500 border-transparent hover:text-slate-800 hover:border-slate-300'
                                            }`}
                                            data-testid={`tab-${tab.key.replace('_', '-')}`}
                                        >
                                            <Icon className="h-4 w-4" />
                                            <span>{tab.label}</span>
                                            <span
                                                className={`inline-flex items-center justify-center min-w-[22px] h-[20px] px-1.5 text-[11px] font-semibold rounded-full ${
                                                    active ? tab.badge : 'bg-slate-100 text-slate-500'
                                                }`}
                                                data-testid={`tab-${tab.key.replace('_', '-')}-count`}
                                            >
                                                {tab.count}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                            <div className="relative max-w-sm">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                <Input
                                    placeholder={t('dashboard.search', 'Search orders...')}
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="pl-9 h-10"
                                    data-testid="search-orders-input"
                                />
                            </div>
                        </div>
                    )}
                    <CardContent className="p-0">
                        {filteredOrders.length === 0 ? (
                            <div className="empty-state py-16">
                                <Package className="h-12 w-12 text-slate-300 mb-4" />
                                <h3 className="text-lg font-medium text-slate-900 mb-1" style={{ fontFamily: 'Inter, sans-serif' }}>
                                    {!isAdmin && orders.length === 0
                                        ? t('dashboard.welcome', 'Welcome! No orders yet.')
                                        : t('dashboard.noorders', 'No orders found')}
                                </h3>
                                <p className="text-sm text-muted-foreground mb-4">
                                    {searchQuery 
                                        ? t('dashboard.trydifferentsearch', 'Try a different search term') 
                                        : activeTab === 'completed' 
                                            ? 'No completed orders yet'
                                            : activeTab === 'cancelled'
                                                ? 'No cancelled orders'
                                                : !isAdmin && orders.length === 0
                                                    ? t('dashboard.getstarted', 'Create your first order to get started.')
                                                    : t('dashboard.createfirst', 'Create your first order')
                                    }
                                </p>
                                {!searchQuery && (activeTab === 'in_progress' || activeTab === 'all') && !isAdmin && (
                                    <Button 
                                        onClick={handleQuickNewOrder}
                                        disabled={isCreating}
                                        className="bg-slate-900 hover:bg-slate-800"
                                        data-testid="empty-state-create-order-btn"
                                    >
                                        <Plus className="h-4 w-4 mr-2" />
                                        {isCreating ? 'Creating...' : t('order.create.button', 'Create Order')}
                                    </Button>
                                )}
                            </div>
                        ) : (
                            <Table>
                                <TableHeader>
                                    <TableRow className="bg-background hover:bg-background">
                                        <TableHead className="text-xs font-medium uppercase tracking-wider text-slate-500 h-10 w-24" aria-sort={isAdmin ? getAriaSort('order_number', sortBy, sortDir) : undefined}>
                                            {isAdmin ? (
                                                <SortableHead column="order_number" label={t('table.orderno', 'Order #')} sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                                            ) : t('table.orderno', 'Order #')}
                                        </TableHead>
                                        <TableHead className="text-xs font-medium uppercase tracking-wider text-slate-500 w-20" aria-sort={isAdmin ? getAriaSort('product_type', sortBy, sortDir) : undefined}>
                                            {isAdmin ? (
                                                <SortableHead column="product_type" label={t('table.type', 'Type')} sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                                            ) : t('table.type', 'Type')}
                                        </TableHead>
                                        <TableHead className="text-xs font-medium uppercase tracking-wider text-slate-500 w-28" aria-sort={isAdmin ? getAriaSort('catalogue_number', sortBy, sortDir) : undefined}>
                                            {isAdmin ? (
                                                <SortableHead column="catalogue_number" label={t('table.catalogue', 'Cat. No.')} sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                                            ) : t('table.catalogue', 'Cat. No.')}
                                        </TableHead>
                                        <TableHead className="text-xs font-medium uppercase tracking-wider text-slate-500" aria-sort={isAdmin ? getAriaSort('title', sortBy, sortDir) : undefined}>
                                            {isAdmin ? (
                                                <SortableHead column="title" label={t('table.title', 'Title')} sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                                            ) : t('table.title', 'Title')}
                                        </TableHead>
                                        {isAdmin && (
                                            <TableHead className="text-xs font-medium uppercase tracking-wider text-slate-500" aria-sort={getAriaSort('customer', sortBy, sortDir)}>
                                                <SortableHead column="customer" label={t('table.customer', 'Customer')} sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                                            </TableHead>
                                        )}
                                        <TableHead className="text-xs font-medium uppercase tracking-wider text-slate-500 w-24" aria-sort={isAdmin ? getAriaSort('artwork', sortBy, sortDir) : undefined}>
                                            {isAdmin ? (
                                                <SortableHead column="artwork" label={t('table.artwork', 'Artwork')} sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                                            ) : t('table.artwork', 'Artwork')}
                                        </TableHead>
                                        <TableHead className="text-xs font-medium uppercase tracking-wider text-slate-500 w-20" aria-sort={isAdmin ? getAriaSort('master', sortBy, sortDir) : undefined}>
                                            {isAdmin ? (
                                                <SortableHead column="master" label={t('table.master', 'Master')} sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                                            ) : t('table.master', 'Master')}
                                        </TableHead>
                                        <TableHead className="text-xs font-medium uppercase tracking-wider text-slate-500" aria-sort={isAdmin ? getAriaSort('status', sortBy, sortDir) : undefined}>
                                            {isAdmin ? (
                                                <SortableHead column="status" label={t('table.status', 'Status')} sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                                            ) : t('table.status', 'Status')}
                                        </TableHead>
                                        <TableHead className="text-xs font-medium uppercase tracking-wider text-slate-500" aria-sort={isAdmin ? getAriaSort('delivery', sortBy, sortDir) : undefined}>
                                            {isAdmin ? (
                                                <SortableHead column="delivery" label={t('table.delivery', 'Delivery')} sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                                            ) : t('table.delivery', 'Delivery')}
                                        </TableHead>
                                        <TableHead className="text-xs font-medium uppercase tracking-wider text-slate-500 w-10">
                                        </TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filteredOrders.map((order) => {
                                        // Determine where to navigate based on order state and product type
                                        const isSimpleMedia = ['cd', 'dvd', 'bluray'].includes(order.product_type);
                                        const hasConfig = order.config_selections && Object.keys(order.config_selections).length > 0;
                                        
                                        let targetPath;
                                        if (order.status === 'Draft') {
                                            if (isSimpleMedia) {
                                                // Simple media orders go to files/workspace
                                                targetPath = `/orders/${order.id}/files`;
                                            } else {
                                                // Vinyl orders: go to configurator if no config, else files
                                                targetPath = hasConfig ? `/orders/${order.id}/files` : `/orders/${order.id}/product`;
                                            }
                                        } else {
                                            // Non-draft orders go to detail page.
                                            // When the admin opened the row from
                                            // the TP dispatch review bucket,
                                            // deep-link to the Supplier → Test
                                            // Press section so the dispatch is
                                            // one scroll from where they
                                            // landed. The ``#test-press`` hash
                                            // is already handled by
                                            // OrderDetail / OrderFiles.
                                            const tpDnvIds = actionsSummary?.tp_dispatch_needs_verification_order_ids || [];
                                            const fromTpDnvBucket = (
                                                attentionFilters.includes('tp_dispatch_needs_verification')
                                                && tpDnvIds.includes(order.id)
                                            );
                                            // "Orders awaiting Sony placement"
                                            // drill-down → open the admin
                                            // Supplier → Production Order
                                            // section directly so the admin
                                            // can send the PO without hunting
                                            // for the card. ``#supplier`` is
                                            // registered in OrderDetailPage's
                                            // hash deep-link handler and
                                            // auto-opens the AdminCollapsible
                                            // section.
                                            const oaspIds = actionsSummary?.orders_awaiting_sony_placement_order_ids || [];
                                            const fromOaspBucket = (
                                                attentionFilters.includes('orders_awaiting_sony_placement')
                                                && oaspIds.includes(order.id)
                                            );
                                            // Customer: when the Action Center
                                            // drill-down (quickFilter) is
                                            // ``test-press-review`` the row
                                            // should open the Test Press card
                                            // directly, mirroring the single-
                                            // order deep-link behaviour.
                                            const fromTpReview = (
                                                quickFilter === 'test-press-review'
                                                && order.test_press_status === 'arrived'
                                            );
                                            if (fromTpDnvBucket) {
                                                targetPath = `/orders/${order.id}#test-press`;
                                            } else if (fromOaspBucket) {
                                                targetPath = `/orders/${order.id}#supplier`;
                                            } else if (fromTpReview) {
                                                targetPath = `/orders/${order.id}/files#test-press`;
                                            } else {
                                                targetPath = `/orders/${order.id}`;
                                            }
                                        }
                                        
                                        const isCompleted = COMPLETED_STATUSES.includes(order.status);
                                        
                                        return (
                                        <TableRow 
                                            key={order.id} 
                                            className={`table-row-hover cursor-pointer h-14 ${isCompleted ? 'opacity-50 bg-slate-50' : ''}`}
                                            onClick={() => navigate(targetPath)}
                                            data-testid={`order-row-${order.id}`}
                                        >
                                            <TableCell className="font-mono text-sm text-slate-600">
                                                {formatOrderNumber(order.order_number)}
                                            </TableCell>
                                            <TableCell>
                                                <span className="inline-flex items-center gap-1 text-xs">
                                                    {order.product_type === 'vinyl' && <span title="Vinyl">🎵</span>}
                                                    {order.product_type === 'cd' && <span title="CD"><MediaDiscIcon type="cd" size={16} /></span>}
                                                    {order.product_type === 'dvd' && <span title="DVD"><MediaDiscIcon type="dvd" size={16} /></span>}
                                                    {order.product_type === 'bluray' && <span title="Blu-ray"><MediaDiscIcon type="bluray" size={16} /></span>}
                                                    {!order.product_type && <span title="Vinyl">🎵</span>}
                                                    <span className="text-slate-600 capitalize">
                                                        {order.product_type === 'bluray' ? 'Blu-ray' : (order.product_type || 'vinyl')}
                                                    </span>
                                                </span>
                                            </TableCell>
                                            <TableCell className="text-sm text-slate-600">
                                                {order.catalogue_number || <span className="text-slate-400">—</span>}
                                            </TableCell>
                                            <TableCell className="font-medium text-slate-900 max-w-[260px]">
                                                <div className="flex items-center gap-2 flex-nowrap min-w-0">
                                                    <div className="flex flex-col min-w-0 flex-1">
                                                        <span
                                                            className="truncate flex items-center gap-1.5"
                                                            data-testid={`order-title-${order.id}`}
                                                            title={order.title}
                                                        >{order.title}<DemoBadge order={order} size="sm" /></span>
                                                        {/* Artist surfaced under the title only when the
                                                            order has one — legacy orders without an
                                                            artist field keep the original single-line look. */}
                                                        {order.artist && order.artist.trim() && (
                                                            <span
                                                                className="text-xs text-slate-500 truncate font-normal"
                                                                data-testid={`order-artist-${order.id}`}
                                                                title={order.artist}
                                                            >
                                                                {order.artist}
                                                            </span>
                                                        )}
                                                    </div>
                                                    {order.notes && order.notes.trim() && (
                                                        <HoverCard openDelay={120} closeDelay={80}>
                                                            <HoverCardTrigger asChild>
                                                                <button
                                                                    type="button"
                                                                    className="inline-flex flex-shrink-0 rounded p-0.5 hover:bg-amber-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400"
                                                                    aria-label="Customer note attached — hover to read"
                                                                    data-testid={`customer-note-indicator-${order.id}`}
                                                                    onClick={(e) => {
                                                                        // Clicking the icon shouldn't open the order
                                                                        // — keep the row click free for navigation.
                                                                        e.stopPropagation();
                                                                    }}
                                                                >
                                                                    <MessageSquare className="h-3.5 w-3.5 text-amber-600" />
                                                                </button>
                                                            </HoverCardTrigger>
                                                            <HoverCardContent
                                                                align="start"
                                                                side="top"
                                                                className="w-80 max-w-[min(20rem,calc(100vw-2rem))] p-3"
                                                                data-testid={`customer-note-tooltip-${order.id}`}
                                                            >
                                                                <div className="text-[11px] font-semibold uppercase tracking-wide text-amber-700 mb-1.5">
                                                                    Customer note
                                                                </div>
                                                                {/*
                                                                    `whitespace-pre-wrap` preserves the customer's line
                                                                    breaks. `break-words` prevents long URLs / SKUs
                                                                    from stretching the popover wider than the
                                                                    max-width above. Content is rendered as text only —
                                                                    no innerHTML — so no XSS surface.
                                                                */}
                                                                <div className="text-sm text-slate-700 whitespace-pre-wrap break-words max-h-48 overflow-y-auto">
                                                                    {order.notes}
                                                                </div>
                                                            </HoverCardContent>
                                                        </HoverCard>
                                                    )}
                                                    {order.order_type === 'reorder' && (
                                                        <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-blue-100 text-blue-700 whitespace-nowrap" data-testid={`order-type-badge-${order.id}`}>
                                                            <RefreshCw className="h-2.5 w-2.5 inline mr-0.5" />
                                                            Re-order
                                                        </span>
                                                    )}
                                                </div>
                                            </TableCell>
                                            {isAdmin && (
                                                <TableCell className="text-sm text-slate-600">
                                                    {order.organization_name || order.customer_name}
                                                </TableCell>
                                            )}
                                            <TableCell>
                                                <div className="flex flex-col gap-0.5">
                                                    <ArtworkStatusBadge 
                                                        status={order.artwork_status} 
                                                        reviewStatus={order.artwork_review_status}
                                                        rejectedCount={order.artwork_rejected_count}
                                                        orderId={order.id}
                                                        reusable={order.reusable_artwork}
                                                        onClick={() => navigate(`/orders/${order.id}/artwork-review`)}
                                                    />
                                                    {/* Artwork supplier status sub-badge — mirrors the Master
                                                        column. Only rendered when the order has at least one
                                                        approved/uploaded artwork and the admin has set a
                                                        supplier-side status (Sony Received / Awaiting / Error).
                                                        Hidden from customers — supplier workflow is an internal
                                                        production detail with no value for them. */}
                                                    {isAdmin && order.artwork_supplier_status && (
                                                        <span className={`asset-badge ${
                                                            order.artwork_supplier_status === 'received' ? 'asset-received' :
                                                            order.artwork_supplier_status === 'error' ? 'asset-error' :
                                                            order.artwork_supplier_status === 'awaiting' ? 'asset-awaiting' :
                                                            'asset-none'
                                                        }`}
                                                            data-testid="artwork-supplier-badge"
                                                        >
                                                            {order.artwork_supplier_status === 'received' ? 'Received' :
                                                             order.artwork_supplier_status === 'error' ? 'Error' :
                                                             order.artwork_supplier_status === 'awaiting' ? 'Awaiting' :
                                                             '—'}
                                                        </span>
                                                    )}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex flex-col gap-0.5">
                                                    {order.needs_master_revision ? (
                                                        <span
                                                            className="asset-badge"
                                                            style={{ background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', fontWeight: 600 }}
                                                            title={isAdmin
                                                                ? 'One or more master files were rejected and require replacement.'
                                                                : 'A master file needs to be replaced.'}
                                                            data-testid="master-badge-revision-needed"
                                                        >
                                                            {isAdmin ? t('status.revision_needed', 'Revision Needed') : t('status.action_required', 'Action Required')}
                                                        </span>
                                                    ) : order.has_audio_master ? (
                                                        <span className="asset-badge asset-received" data-testid="master-badge">
                                                            {t('status.received', 'Received')}
                                                        </span>
                                                    ) : order.reusable_master ? (
                                                        <span className="asset-badge asset-ready" data-testid="master-badge-ready">
                                                            {t('status.ready', 'Ready')}
                                                        </span>
                                                    ) : (
                                                        <span className="asset-badge asset-missing" data-testid="master-badge-missing">
                                                            {t('status.missing', 'Missing')}
                                                        </span>
                                                    )}
                                                    {isAdmin && order.has_audio_master && !order.needs_master_revision && order.audio_supplier_status && (
                                                        <span className={`asset-badge ${
                                                            order.audio_supplier_status === 'received' ? 'asset-received' :
                                                            order.audio_supplier_status === 'error' ? 'asset-error' :
                                                            order.audio_supplier_status === 'uploaded' ? 'asset-uploaded' :
                                                            order.audio_supplier_status === 'awaiting' ? 'asset-awaiting' :
                                                            'asset-none'
                                                        }`}>
                                                            {order.audio_supplier_status === 'received' ? 'Received' :
                                                             order.audio_supplier_status === 'error' ? 'Error' :
                                                             order.audio_supplier_status === 'uploaded' ? 'Uploaded' :
                                                             order.audio_supplier_status === 'awaiting' ? 'Awaiting' :
                                                             '—'}
                                                        </span>
                                                    )}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-1.5">
                                                    <OrderStatusBadge
                                                        status={order.status}
                                                        onHoldReason={order.on_hold_reason}
                                                        onHoldAt={order.on_hold_at}
                                                        onHoldBy={order.on_hold_by}
                                                    />
                                                    {isAdmin && (
                                                        <FinanceStatusBadge 
                                                            invoiceEconomicStatus={order.invoice_economic_status} 
                                                            invoiceStatus={order.invoice_status}
                                                        />
                                                    )}
                                                    {order.invoice_payment_status && (
                                                        <InvoicePaymentBadge status={order.invoice_payment_status} />
                                                    )}
                                                    <TestPressBadge
                                                        status={order.test_press_status}
                                                        expectedArrival={order.test_press_expected_arrival}
                                                        viewerRole={isAdmin ? 'admin' : 'customer'}
                                                        sonyResponseBranch={order.test_press_sony_response_branch}
                                                        tpVersion={order.test_press_version}
                                                    />
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <DeliveryStatus order={order} />
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-1">
                                                    {/* Delete button for draft orders — visible to customers
                                                        on their own drafts and to admins/super_admins on any draft. */}
                                                    {order.status === 'Draft' && (
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            className="h-8 w-8 text-slate-400 hover:text-red-600 hover:bg-red-50"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setDeleteOrderId(order.id);
                                                                setDeleteOrderTitle(order.title);
                                                            }}
                                                            data-testid={`delete-draft-order-${order.id}`}
                                                        >
                                                            <Trash2 className="h-4 w-4" />
                                                        </Button>
                                                    )}
                                                    <ChevronRight className="h-4 w-4 text-slate-400" />
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        )}
                    </CardContent>
                </Card>
                )}

                {/* Delete Draft Order Confirmation Dialog (Customer) */}
                <Dialog open={!!deleteOrderId} onOpenChange={(open) => !open && setDeleteOrderId(null)}>
                    <DialogContent className="sm:max-w-md">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2 text-red-600">
                                <Trash2 className="h-5 w-5" />
                                Delete Draft Order?
                            </DialogTitle>
                            <DialogDescription>
                                Are you sure you want to delete this draft order? This action cannot be undone.
                            </DialogDescription>
                        </DialogHeader>
                        <div className="py-4">
                            <p className="text-sm text-slate-600">
                                Order: <span className="font-medium text-slate-900">{deleteOrderTitle}</span>
                            </p>
                        </div>
                        <div className="flex justify-end gap-3">
                            <Button
                                variant="outline"
                                onClick={() => setDeleteOrderId(null)}
                                disabled={isDeleting}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="destructive"
                                onClick={handleDeleteDraftOrder}
                                disabled={isDeleting}
                                data-testid="confirm-delete-draft-order"
                            >
                                {isDeleting ? (
                                    <>
                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                        Deleting...
                                    </>
                                ) : (
                                    'Delete Order'
                                )}
                            </Button>
                        </div>
                    </DialogContent>
                </Dialog>
                
                {/* Product Type Selector Modal */}
                <Dialog open={showProductTypeModal} onOpenChange={setShowProductTypeModal}>
                    <DialogContent className="max-w-lg">
                        <DialogHeader>
                            <DialogTitle>{t('dashboard.create_new_order', 'Create New Order')}</DialogTitle>
                            <DialogDescription>
                                Select the type of order you want to create
                            </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-3 py-4">
                            {availableOrderTypes
                                .filter(type => enabledOrderTypes.includes(type.id))
                                .map((type) => (
                                    <button
                                        key={type.id}
                                        onClick={() => handleProductTypeSelect(type.id)}
                                        className="w-full p-4 rounded-lg border border-slate-200 hover:border-blue-300 hover:bg-blue-50 transition-colors text-left flex items-center gap-4"
                                        data-testid={`select-order-type-${type.id}`}
                                    >
                                        <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center text-2xl">
                                            {type.id === 'vinyl' && '🎵'}
                                            {type.id === 'cd' && <MediaDiscIcon type="cd" size={28} />}
                                            {type.id === 'dvd' && <MediaDiscIcon type="dvd" size={28} />}
                                            {type.id === 'bluray' && <MediaDiscIcon type="bluray" size={28} />}
                                        </div>
                                        <div className="flex-1">
                                            <p className="font-medium text-slate-900">{type.label}</p>
                                            <p className="text-sm text-slate-500">
                                                {type.id === 'vinyl' 
                                                    ? 'Full vinyl configuration builder'
                                                    : 'Simple media order'}
                                            </p>
                                        </div>
                                        <ChevronRight className="h-5 w-5 text-slate-400" />
                                    </button>
                                ))}
                        </div>
                    </DialogContent>
                </Dialog>

                {/* Order-value breakdown Dialog — reused for both
                    "Total Active Order Value" and "Open Uninvoiced Order
                    Value". `breakdownMetric` decides which dataset renders. */}
                {(() => {
                    const isActiveView = breakdownMetric === 'active';
                    const metric = isActiveView ? totalActiveOrderValue : openUninvoicedOrderValue;
                    const modalTitle = isActiveView ? 'Total Active Order Value — Breakdown' : 'Open Uninvoiced Order Value — Breakdown';
                    const modalDesc = isActiveView
                        ? 'All non-draft, non-cancelled orders — full production pipeline value including work that already has a sales document or booked invoice.'
                        : 'Orders that still need to be invoiced. Excludes anything with a local draft sales document, an e-conomic draft invoice, or a booked invoice (those live under Finance).';
                    const accentBg = isActiveView ? 'bg-indigo-50 border-indigo-200' : 'bg-amber-50 border-amber-200';
                    const accentLine = isActiveView ? 'border-indigo-200' : 'border-amber-200';
                    const accentIcon = isActiveView
                        ? <FileText className="h-5 w-5 text-indigo-600" />
                        : <AlertCircle className="h-5 w-5 text-amber-600" />;

                    const stateLabel = {
                        booked: { text: 'Booked', cls: 'bg-emerald-100 text-emerald-800' },
                        economic_draft: { text: 'E-conomic Draft', cls: 'bg-blue-100 text-blue-800' },
                        local_draft: { text: 'Local Draft', cls: 'bg-slate-100 text-slate-700' },
                        none: { text: 'Uninvoiced', cls: 'bg-amber-100 text-amber-800' },
                    };

                    // ----- Sort the details client-side per the sortable
                    // column header state. Rows with a null margin/profit
                    // always sink to the bottom of the current view so
                    // uncosted orders don't hide the profitable ones.
                    const toggleSort = (field) => {
                        setBreakdownSort((prev) => {
                            if (prev.field !== field) return { field, dir: 'desc' };
                            return { field, dir: prev.dir === 'desc' ? 'asc' : 'desc' };
                        });
                    };
                    const _valueFor = (row) => {
                        if (breakdownSort.field === 'gross_profit') return row.gross_profit_eur;
                        if (breakdownSort.field === 'margin') return row.gross_margin_pct;
                        return row.dkk;
                    };
                    const sortedDetails = [...metric.details].sort((a, b) => {
                        const av = _valueFor(a);
                        const bv = _valueFor(b);
                        const aNull = av == null;
                        const bNull = bv == null;
                        if (aNull && bNull) return 0;
                        if (aNull) return 1;   // nulls always last
                        if (bNull) return -1;
                        return breakdownSort.dir === 'desc' ? (bv - av) : (av - bv);
                    });

                    const renderSortIcon = (field) => {
                        if (breakdownSort.field !== field) {
                            return <ArrowUpDown className="inline h-3 w-3 ml-1 text-slate-300" />;
                        }
                        return breakdownSort.dir === 'desc'
                            ? <ArrowDown className="inline h-3 w-3 ml-1 text-slate-500" />
                            : <ArrowUp className="inline h-3 w-3 ml-1 text-slate-500" />;
                    };

                    const formatEur0 = (n) => (
                        (n ?? 0).toLocaleString('da-DK', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
                    );

                    return (
                        <Dialog open={showOpenOrderBreakdown} onOpenChange={setShowOpenOrderBreakdown}>
                            <DialogContent className="sm:max-w-5xl max-h-[85vh] flex flex-col">
                                <DialogHeader>
                                    <DialogTitle className="flex items-center gap-2">
                                        {accentIcon}
                                        {modalTitle}
                                    </DialogTitle>
                                    <DialogDescription>{modalDesc}</DialogDescription>
                                </DialogHeader>

                                <div className="flex-1 overflow-y-auto -mx-6 px-6">
                                    <div className={`mb-3 flex items-center gap-4 text-sm ${accentBg} border rounded-lg p-3`}>
                                        <div>
                                            <div className="text-xs text-slate-500">Total DKK</div>
                                            <div className="text-base font-semibold text-slate-900" data-testid="breakdown-total-dkk">
                                                {metric.dkk.toLocaleString('da-DK', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} DKK
                                            </div>
                                        </div>
                                        <div className={`border-l ${accentLine} pl-4`}>
                                            <div className="text-xs text-slate-500">Total EUR</div>
                                            <div className="text-base font-semibold text-slate-900" data-testid="breakdown-total-eur">
                                                {metric.eur.toLocaleString('da-DK', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} EUR
                                            </div>
                                        </div>
                                        <div className={`border-l ${accentLine} pl-4`}>
                                            <div className="text-xs text-slate-500">Orders</div>
                                            <div className="text-base font-semibold text-slate-900" data-testid="breakdown-count">{metric.count}</div>
                                        </div>
                                    </div>

                                    {metric.details.length === 0 ? (
                                        <div className="py-10 text-center text-sm text-slate-500">
                                            {isActiveView ? 'No active orders right now.' : 'Nothing left to invoice — everything with a sales document or booked invoice.'}
                                        </div>
                                    ) : (
                                        <table className="w-full text-sm">
                                            <thead className="text-xs text-slate-500 uppercase tracking-wide border-b">
                                                <tr>
                                                    <th className="text-left py-2 font-medium">Order</th>
                                                    <th className="text-left py-2 font-medium">Status</th>
                                                    <th className="text-left py-2 font-medium">Invoice</th>
                                                    <th
                                                        className="text-right py-2 font-medium cursor-pointer select-none hover:text-slate-700"
                                                        onClick={() => toggleSort('dkk')}
                                                        data-testid="breakdown-sort-dkk"
                                                        title="Sort by order value (DKK)"
                                                    >
                                                        DKK {renderSortIcon('dkk')}
                                                    </th>
                                                    <th className="text-right py-2 font-medium">EUR</th>
                                                    <th
                                                        className="text-right py-2 font-medium cursor-pointer select-none hover:text-slate-700"
                                                        onClick={() => toggleSort('gross_profit')}
                                                        data-testid="breakdown-sort-gross-profit"
                                                        title="Sort by Gross Profit"
                                                    >
                                                        Gross Margin {renderSortIcon('gross_profit')}
                                                    </th>
                                                    <th
                                                        className="text-right py-2 font-medium cursor-pointer select-none hover:text-slate-700 pl-1"
                                                        onClick={() => toggleSort('margin')}
                                                        data-testid="breakdown-sort-margin-pct"
                                                        title="Sort by Margin %"
                                                    >
                                                        % {renderSortIcon('margin')}
                                                    </th>
                                                    <th className="text-left py-2 font-medium pl-4">Reason</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y">
                                                {sortedDetails.map((d) => {
                                                    const lbl = stateLabel[d.invoice_state] || stateLabel.none;
                                                    // Colour margin cells so profitable vs. thin orders
                                                    // are legible at a glance. Threshold mirrors the
                                                    // 20% low-margin badge on the orders table.
                                                    const marginPct = d.gross_margin_pct;
                                                    const profitEur = d.gross_profit_eur;
                                                    let marginCls = 'text-slate-400';
                                                    if (marginPct != null) {
                                                        if (marginPct < 20) marginCls = 'text-rose-600 font-medium';
                                                        else if (marginPct < 35) marginCls = 'text-amber-600';
                                                        else marginCls = 'text-emerald-700 font-medium';
                                                    }
                                                    let profitCls = 'text-slate-400';
                                                    if (profitEur != null) {
                                                        profitCls = profitEur < 0 ? 'text-rose-600 font-medium' : 'text-slate-900';
                                                    }
                                                    return (
                                                        <tr
                                                            key={d.id}
                                                            className="hover:bg-slate-50 cursor-pointer"
                                                            onClick={() => { setShowOpenOrderBreakdown(false); navigate(`/orders/${d.id}`); }}
                                                            data-testid={`breakdown-row-${d.order_number}`}
                                                        >
                                                            <td className="py-2">
                                                                <div className="font-medium text-slate-900">#{String(d.order_number).padStart(6, '0')}</div>
                                                                <div className="text-xs text-slate-500 truncate max-w-[260px]">{d.title}</div>
                                                            </td>
                                                            <td className="py-2">
                                                                <OrderStatusBadge
                                                                    status={d.status}
                                                                    onHoldReason={d.on_hold_reason}
                                                                    onHoldAt={d.on_hold_at}
                                                                    onHoldBy={d.on_hold_by}
                                                                />
                                                            </td>
                                                            <td className="py-2">
                                                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium ${lbl.cls}`}>
                                                                    {lbl.text}
                                                                </span>
                                                            </td>
                                                            <td className="py-2 text-right tabular-nums">
                                                                {formatEur0(d.dkk)}
                                                            </td>
                                                            <td className="py-2 text-right tabular-nums text-slate-500">
                                                                {formatEur0(d.eur)}
                                                            </td>
                                                            <td
                                                                className={`py-2 text-right tabular-nums ${profitEur == null ? '' : profitCls}`}
                                                                data-testid={`breakdown-row-${d.order_number}-gross-profit`}
                                                            >
                                                                {profitEur != null ? (
                                                                    `€${formatEur0(profitEur)}`
                                                                ) : (
                                                                    <span
                                                                        className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5"
                                                                        title={d.gross_margin_reason || 'No margin data available'}
                                                                        data-testid={`breakdown-row-${d.order_number}-gross-profit-reason`}
                                                                    >
                                                                        {d.gross_margin_reason || '—'}
                                                                    </span>
                                                                )}
                                                            </td>
                                                            <td
                                                                className={`py-2 text-right tabular-nums pl-1 ${marginPct == null ? 'text-slate-400' : marginCls}`}
                                                                data-testid={`breakdown-row-${d.order_number}-margin-pct`}
                                                            >
                                                                {marginPct != null ? `${marginPct.toFixed(1)}%` : '—'}
                                                            </td>
                                                            <td className="py-2 pl-4 text-xs text-slate-500 max-w-[240px]">
                                                                {d.inclusion_reason}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                            </DialogContent>
                        </Dialog>
                    );
                })()}
            </main>
        </div>
    );
}
